import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  WEEKDAYS,
  formatRecurrence,
  parseRecurrence,
  weekdayOf,
  type EventParticipantStatus,
  type Frequency,
  type Weekday,
} from "@vmeste/shared";
import { useSession } from "../app/session";
import { useLists } from "../app/lists";
import type { ListItemView, ListView } from "../app/views";
import { useChat } from "../app/chat";
import { PANE_HEIGHT, TAB_BAR_MARGIN } from "../app/chrome";
import {
  addDays,
  addMonths,
  coversDay,
  dayKey,
  draftError,
  draftForDate,
  draftFromEvent,
  eventsOnDay,
  formatDayLabel,
  formatDayShort,
  formatEventTime,
  formatMonthTitle,
  groupByDay,
  monthGrid,
  sameDay,
  sameMonth,
  startOfDay,
  startOfMonth,
  timeValue,
  useCalendar,
  useEventParticipants,
  type DayGroup,
  type EventDraft,
  type EventView,
} from "../app/calendar";
import { AddButton, Button, Card } from "../components/ui";
import { AttachmentStrip } from "../components/AttachmentStrip";
import { ACCEPT_ATTACHMENTS, useAttachments } from "../app/attachments";
import {
  LinkIcon,
  ChatIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  EditIcon,
  PlusIcon,
  RefreshIcon,
  RepeatIcon,
  TrashIcon,
} from "../components/icons";
import {
  useDateFormats,
  useT,
  useWeekdayNames,
  type MessageKey,
} from "../i18n/index.js";
import { api } from "../lib/api";
import { operations } from "../lib/outbox";
import { syncEngine } from "../lib/sync";
import { leadWords } from "../components/NotificationSettings";
import { Sheet } from "../components/Sheet";
import { ScreenHeader } from "../components/ScreenHeader";
import {
  closeDraft,
  minimiseDraft,
  openDraft,
  patchDraft,
  useDraftValues,
} from "../app/draft";
import { cn } from "../lib/cn";

/**
 * Календарь (SPEC §4.4).
 *
 * The agenda leads. Two people on a phone want to know what is happening today
 * and tomorrow; the month grid answers a different, rarer question — is the
 * 14th free? — and so it is the second tab rather than the front page.
 *
 * A row is one **occurrence**: the rule is expanded in `app/calendar`, so a
 * weekly event is many rows over one stored event. Tapping any of them opens
 * the sheet below, which is where an event's files, its discussion and the day
 * it is not happening live.
 *
 * «Неделя» and «День» are the same component twice — a column per day, with the
 * hour as a unit of height. See `TimeGrid`.
 *
 * Reminders are phase 6 proper (SPEC §10).
 */

type View = "today" | "agenda" | "day" | "week" | "month";

/**
 * How much of a repeating event an edit is about.
 *
 * `series` is what saving has always meant and still means for a one-off.
 * `once` is the standard RFC 5545 split: the day leaves the series through
 * `EXDATE` and a rule-less event takes its place. Nothing on the server had to
 * learn a new shape for it — see `saveEdit`.
 */
type EditScope = "series" | "once";

/**
 * How far the agenda reaches.
 *
 * A repeating event has no end, so «Ближайшее» has to stop somewhere. Behind is
 * short because the past section is a glance back at what just happened, not an
 * archive; ahead is a year because that is where a birthday lives.
 */
const AGENDA_BEHIND_DAYS = 90;
const AGENDA_AHEAD_DAYS = 365;

/** The key of each tab's label, not the label — the words are looked up in the
    render, so switching language relabels the tabs. */
const VIEW_LABEL: Record<View, MessageKey> = {
  today: "calendar.viewToday",
  agenda: "calendar.viewUpcoming",
  day: "calendar.viewDay",
  week: "calendar.viewWeek",
  month: "calendar.viewMonth",
};

/**
 * Two tabs, not four.
 *
 * Four labels do not fit across a phone in one row, so the control wrapped to
 * two — and a two-row switcher plus a heading plus a period bar was leaving
 * less than two hours of the day visible on the view whose entire point is
 * showing hours.
 *
 * But the four were never the same kind of choice anyway. «Ближайшее» and the
 * calendar are two ways of reading the same events; day, week and month are one
 * way at three zooms. Mixing them put "what am I looking at" and "how close am
 * I standing" in one row of equals.
 */
/** Long enough to mean it, short enough that a row does not stay armed. */
const CONFIRM_TIMEOUT_MS = 4_000;

/**
 * Three tabs, and «Сегодня» is a third *kind* of reading rather than a fourth
 * zoom — which is why it belongs here and the zooms still do not.
 *
 * «Ближайшее» answers "what is coming up", the calendar answers "is the 14th
 * free", and this one answers the question a household actually opens an app
 * with in the morning: what is today. That is not the same list filtered — it
 * carries the deadlines that fall today too, which live on another screen and
 * are otherwise found only by unfolding every list.
 */
const TABS: readonly View[] = ["today", "agenda", "month"];

/** The zooms, in order of how much they show. Only ever one row, three items. */
const ZOOMS: readonly Exclude<View, "agenda">[] = ["day", "week", "month"];

/**
 * An event being written, and the photographs picked for it.
 *
 * One value rather than two, because both have to survive the sheet being
 * pushed down — and a draft that kept the words and dropped the pictures would
 * be a draft that lies about what it kept.
 */
interface EventComposition {
  draft: EventDraft;
  files: File[];
}

const EVENT_DRAFT = "event.new";

export function CalendarPage() {
  const t = useT();
  const { session } = useSession();
  const spaceId = session?.spaceId ?? null;
  const self = session?.user.id ?? null;

  const { occurrencesIn, loaded, add, update, remove } = useCalendar(spaceId);
  // Read directly, because a memo below has to recompute on every change to the
  // space and not merely re-render with the page.
  const synced = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);
  const { listsFor } = useLists();

  /*
   * The list, not the month.
   *
   * What two people ask this screen is "what is coming up" — a question the
   * agenda answers in one glance and the grid answers by making you count
   * squares. The month is a tap away for the rarer question, "is the 14th
   * free?".
   *
   * This was the month for a while, and the objection then was real: an empty
   * agenda was a paragraph of text and no calendar at all, which reads as a
   * screen that failed rather than a week that is free. That objection is
   * answered where it belongs — the empty agenda now offers the thing somebody
   * would want next — rather than by hiding the useful view behind the pretty
   * one.
   */
  const [view, setView] = useState<View>("agenda");
  /*
   * The zoom outlives a trip to «Ближайшее».
   *
   * It cannot be derived from `view`, which is the whole point: while the
   * agenda is open there is no scale to read it off, and deriving it would
   * quietly snap somebody back to the month every time they glanced at what is
   * coming up.
   */
  const [zoom, setZoom] = useState<Exclude<View, "agenda">>("month");
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState<Date>(() => startOfDay(new Date()));

  /** The draft being composed, or null when the composer is closed. */
  /*
   * The form's state is not this screen's — see `app/draft`. The files picked
   * for it travel with it: a draft that kept the words and dropped the
   * photographs would be a draft that lies about what it kept.
   */
  const composing = useDraftValues<EventComposition>(EVENT_DRAFT);

  /**
   * Whose sheet is open, by `EventView.key` — the **occurrence**, not the
   * event.
   *
   * A repeating event is one id on many rows, so holding the id here opened a
   * sheet on every Tuesday at once. The key is unique per occurrence, and it is
   * also what the sheet needs: «не в этот раз» is about the day the row it came
   * from stands for.
   */
  const [opened, setOpened] = useState<string | null>(null);

  /*
   * Arriving from a search hit or the activity feed.
   *
   * They handed out `/calendar` and nothing more, which on a calendar is worse
   * than on a list: the event found may be three months back, and the screen
   * opens on what is coming up.
   *
   * Held as an id rather than a key, because a search hit knows the event and
   * not which occurrence. The day view is what makes that safe — the day the
   * event starts holds exactly one of its occurrences, so the id can only match
   * one row. Read once and dropped from the address; an arrival is not a place.
   */
  const [params, setParams] = useSearchParams();
  const wantedEvent = params.get("event");

  useEffect(() => {
    if (!wantedEvent || !spaceId) return;

    const row = syncEngine.view(spaceId).events[wantedEvent];
    setParams(new URLSearchParams(), { replace: true });
    if (!row) return;

    setSelected(startOfDay(new Date(row.startsAt)));
    setMonth(startOfMonth(new Date(row.startsAt)));
    setView("day");
    setZoom("day");
    setOpened(wantedEvent);
  }, [wantedEvent, spaceId, setParams]);

  const today = startOfDay(new Date());

  /** The two views that draw a clock, and so need the room. */
  const onScale = view === "week" || view === "day";

  /**
   * How close the calendar is standing, remembered across a trip to the list.
   *
   * Somebody who chose the week does not want the month back every time they
   * glance at what is next.
   */
  const chooseZoom = (next: Exclude<View, "agenda">) => {
    setZoom(next);
    setView(next);
  };

  /**
   * Files chosen before the event exists.
   *
   * Plain `File` objects rather than an `AttachmentStrip`: the strip lists what
   * is attached to something, and until the event has been created there is
   * nothing to attach these to. They become attachments on submit, once the
   * server has answered with an id.
   */
  const { upload } = useAttachments(spaceId);

  /*
   * Repeats become the things actually drawn, over the window each view is
   * about to show. A window is required rather than optional: «каждый день» has
   * no last occurrence, so "give me all of them" is not a question with an
   * answer.
   *
   * The dependency is `today.getTime()` rather than `today`, which is a fresh
   * Date on every render and would defeat the memo entirely.
   */
  /*
   * The agenda shows a repeating event **once**.
   *
   * The window has to stay wide — a birthday eight months out belongs in
   * "what's coming" — and a weekday stand-up expanded across it is two hundred
   * and sixty rows of the same sentence, which pushes the birthday off the
   * bottom and makes the list useless for the thing it exists for.
   *
   * So a series contributes its next occurrence, and one behind us if it has
   * one. The rest are not hidden: they are dots on the month grid, which is
   * the view that answers "when does this happen" — while this one answers
   * "what is next".
   */
  /**
   * Today, and what is already late.
   *
   * Every occurrence of the day rather than one per series — «Сегодня» is a day,
   * and a standing arrangement that happens today happens today.
   */
  const todayEvents = useMemo(
    () => occurrencesIn(today, addDays(today, 1)),
    [occurrencesIn, today],
  );

  /*
   * The deadlines that fall today, and the ones that already fell.
   *
   * They live on the lists screen and are otherwise found only by unfolding
   * every list one at a time — which is what makes this a "today" screen rather
   * than the agenda cut down to one day. Overdue included, and first: a
   * deadline that has passed is more of an answer to "what is today" than one
   * that has not arrived.
   */
  const todayTasks = useMemo(() => {
    const edge = addDays(today, 1).getTime();

    return listsFor(spaceId ?? "")
      .flatMap((list) =>
        list.items
          .filter((item) => !item.done && item.dueAt !== null)
          .filter((item) => new Date(item.dueAt!).getTime() < edge)
          .map((item) => ({ item, list })),
      )
      .sort((a, b) => (a.item.dueAt! < b.item.dueAt! ? -1 : 1));
  }, [listsFor, spaceId, today]);

  /**
   * Who is going to what, in a word per event.
   *
   * «Вдвоём» when everybody is, because that is the common case and a list of
   * both names on every row is two words saying one thing. Neutral: «оба» and
   * «обе» would each be wrong half the time, and a name does not say which.
   *
   * The creator is written in as `going` when the event is made, so this
   * answers «чьё это» from the first second rather than waiting for anybody to
   * be asked.
   */
  const goingLabel = useMemo(() => {
    if (!spaceId) return () => null;

    const state = syncEngine.view(spaceId);
    const names = new Map(state.members.map((m) => [m.userId, m.user.displayName]));

    const going = new Map<string, string[]>();
    for (const row of Object.values(state.eventParticipants)) {
      if (row.status !== "going") continue;
      going.set(row.eventId, [...(going.get(row.eventId) ?? []), row.userId]);
    }

    const labels = new Map<string, string>();
    for (const [eventId, ids] of going) {
      labels.set(
        eventId,
        names.size > 1 && ids.length >= names.size
          ? t("calendar.goingBoth")
          : ids
              .map((id) => (id === self ? t("common.you") : (names.get(id) ?? t("chat.someone"))))
              .join(", "),
      );
    }

    return (eventId: string) => labels.get(eventId) ?? null;
    /*
     * `synced`, and it has to be.
     *
     * This depended on `loaded` first, on the reasoning that it "moves with
     * every delta" — it does not: it goes true once and stays there. So the
     * labels were worked out on the first pull and frozen, and a new event's
     * «Вы» only turned up after leaving the screen and coming back, which
     * remounts and recomputes. The engine's snapshot is the thing that moves
     * whenever anything in the space does, the pending overlay included, which
     * is what makes the marker appear on an event that has not reached the
     * server yet.
     */
  }, [spaceId, self, synced, t]);

  const agendaEvents = useMemo(() => {
    const all = occurrencesIn(
      addDays(today, -AGENDA_BEHIND_DAYS),
      addDays(today, AGENDA_AHEAD_DAYS),
    );

    const nextOf = new Map<string, EventView>();
    const lastOf = new Map<string, EventView>();
    const once: EventView[] = [];

    // Ascending, so the first future occurrence of a series is its next and
    // every past one overwrites the previous — leaving the most recent.
    for (const event of all) {
      if (!event.repeating) {
        once.push(event);
      } else if (event.startsAt >= today) {
        if (!nextOf.has(event.id)) nextOf.set(event.id, event);
      } else {
        lastOf.set(event.id, event);
      }
    }

    return [...once, ...nextOf.values(), ...lastOf.values()].sort(
      (a, b) => a.startsAt.getTime() - b.startsAt.getTime(),
    );
  }, [occurrencesIn, today.getTime()]);

  const monthEvents = useMemo(() => {
    const cells = monthGrid(month);
    return occurrencesIn(cells[0]!, addDays(cells[cells.length - 1]!, 1));
  }, [occurrencesIn, month]);

  /**
   * The days the time grid is about: one, or a Monday-first week around the
   * selection. Empty for the two views that have no hour scale.
   */
  const gridDays = useMemo(() => {
    if (view === "day") return [startOfDay(selected)];
    if (view === "week") {
      const first = startOfWeek(selected);
      return Array.from({ length: 7 }, (_, i) => addDays(first, i));
    }
    return [];
  }, [view, selected]);

  /*
   * Exactly the window on screen, and no wider.
   *
   * An occurrence that began yesterday evening and runs past midnight is
   * already in it: `expandOccurrences` keeps anything whose *end* reaches the
   * window, not only what starts inside it. That is what lets a night out show
   * on both of the days it touches.
   */
  const gridEvents = useMemo(() => {
    const first = gridDays[0];
    if (!first) return [];
    return occurrencesIn(first, addDays(first, gridDays.length));
  }, [occurrencesIn, gridDays]);

  // Only ever one form open: two of them on a phone screen is two places to
  // type and one of them silently thrown away.
  //
  // `time` is the hour a tap on empty grid landed on. `draftForDate` fills the
  // start with "an hour from now", which is the right guess for a button and
  // the wrong one for a slot somebody pointed at.
  function compose(date: Date, time?: string) {
    setOpened(null);
    const draft = draftForDate(date);
    openDraft<EventComposition>({
      key: EVENT_DRAFT,
      path: "/calendar",
      title: t("calendar.newEvent"),
      values: { draft: time ? { ...draft, startTime: time } : draft, files: [] },
      isReady: (values) => values.draft.title.trim().length > 0,
      submit: (values) => saveEvent(values),
    });

    // The composer opens above the grid, and the grid is what the person was
    // looking at — on a phone, scrolled well past the top of the page. Without
    // this, tapping an empty slot reads as nothing having happened at all.
    if (time) window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /**
   * Move the day the narrow views are about — and the month with it, so that
   * paging a week into September and then switching to «Месяц» does not land
   * back in August.
   */
  function goToDate(date: Date) {
    const day = startOfDay(date);
    setSelected(day);
    setMonth(startOfMonth(day));
  }

  function openSheet(key: string | null) {
    // Opening an existing event puts away a half-written new one rather than
    // discarding it: two sheets at once is not a state this screen has, and
    // losing what somebody typed to see something else is not a trade.
    if (key !== null) minimiseDraft();
    setOpened(key);
  }

  /**
   * Write an edit made in the sheet, at the scope the person chose.
   *
   * «Всю серию» is `update` — the one behaviour this screen used to have, and
   * still the only one a one-off can mean. «Только этот раз» splits instead, and
   * the split lives here rather than in the sheet because this is where `add`
   * is: creating the replacement is a write like any other.
   *
   * **The order is the point, and it is not cosmetic.** The replacement is
   * created first, then it takes over the day's files and answers, and the day
   * is dropped from the series only once both have landed — `add` resolves when
   * the operation actually settles, so offline it simply waits rather than
   * resolving early. Cancelling the day first would mean a create that never
   * lands leaves a hole with nothing in it: an edit that quietly deletes an
   * evening. This way the worst failure is two entries on one day, which is
   * visible and can be undone.
   */
  /**
   * Save an event and the files that were picked for it.
   *
   * The files wait for the event, and cannot do otherwise: the server refuses
   * to attach anything to a row it has never heard of, and a new event goes
   * out through the outbox. `add` answers with the id once the server actually
   * has it; that is what these files are waiting for — the same shape as
   * sending a message with a photograph. The event itself appears instantly,
   * from the pending overlay. Only the files wait.
   *
   * Named rather than inline because the collapsed draft line saves from
   * outside this screen, with nothing of it mounted.
   */
  function saveEvent({ draft, files }: EventComposition) {
    void (async () => {
      const id = await add(draft);
      if (!id) return;
      for (const file of files) await upload({ type: "event", id }, file);
    })();
  }

  function saveEdit(event: EventView, draft: EventDraft, scope: EditScope) {
    if (scope === "series" || !spaceId) {
      update(event.id, draft);
      return;
    }

    void (async () => {
      // Without a rule: this one is the day itself, not a series of its own.
      const id = await add({ ...draft, rrule: null });
      if (!id) return;

      /*
       * The tickets, the photo of the invitation, and who said they were
       * coming. Only the server can move them: the bytes of a file are not on
       * this device, so nothing here could carry one over.
       *
       * Between the create and the skip, and both of those placements matter.
       * After the create because there is no event to carry them onto until it
       * exists; before the skip because the series still holds the day while
       * this runs — so if the call is cut off, the evening is still on the
       * calendar with everything on it, and what is left over is a duplicate
       * entry the person can see and delete. The other order would trade that
       * for an evening stripped of its files with nothing saying so, which is
       * the failure this whole call exists to close.
       *
       * The edit itself is not undone either way: the replacement is already
       * saved and already on the day.
       */
      try {
        await api.inheritEventDetails(spaceId, id, event.id);
      } catch {
        return;
      }

      skipDay(spaceId, event);
    })();
  }

  return (
    <div className="flex flex-col gap-4 px-4 pt-4 pb-8">
      <ScreenHeader
        title={t("layout.calendar")}
        subtitle={t("calendar.subtitle")}
        /* The strapline used to stand down for the time grids — twenty pixels
           are two hours of a day on a phone. It stays now, because those
           twenty pixels were also what made the «+» hop when you came here
           from the lists: the block under a title is what holds every screen's
           button at the same height. */
        action={
          <AddButton
            dimmed={composing !== null}
            label={t("calendar.newEvent")}
            onClick={() => compose(view === "agenda" ? today : selected)}
          />
        }
      />

      {/* A pill holding pills. The track is a tonal step off the ground rather
          than a bordered box, and the chosen segment is amber — the raised
          white "tab" the old language used would be a second thing claiming to
          be a surface right under the one that is. */}
      <div
        role="tablist"
        aria-label={t("calendar.viewLabel")}
        className="flex gap-1 rounded-pill bg-surface-alt p-1.5"
      >
        {TABS.map((value) => (
          <button
            key={value}
            role="tab"
            aria-selected={value === view || (value === "month" && onScale)}
            // Coming back to the calendar returns to the zoom that was last
            // open. Snapping to the month every time would undo a choice
            // somebody made one tap ago.
            onClick={() => setView(value === "month" ? zoom : value)}
            className={cn(
              "min-h-[var(--touch)] flex-1 rounded-pill px-2 text-[0.9375rem] transition-colors",
              value === view || (value === "month" && onScale)
                ? "bg-accent-soft font-semibold text-accent-ink"
                : "font-medium text-muted hover:text-ink-soft",
            )}
          >
            {/* «Календарь» rather than «Месяц» on this row: the tab stands for
                all three zooms, and naming it after one of them would make the
                zoom below look like a contradiction. */}
            {t(
              value === "today"
                ? "calendar.viewToday"
                : value === "agenda"
                  ? "calendar.viewUpcoming"
                  : "layout.calendar",
            )}
          </button>
        ))}
      </div>

      {composing ? (
        <Sheet
          title={t("calendar.newEvent")}
          onClose={closeDraft}
          onMinimise={minimiseDraft}
        >
          <EventForm
            draft={composing.draft}
            submitLabel={t("common.add")}
            onChange={(next) => patchDraft({ ...composing, draft: next })}
            onSubmit={(next) => {
              closeDraft();
              saveEvent({ ...composing, draft: next });
            }}
            onCancel={closeDraft}
          >
            <PendingFiles
              files={composing.files}
              onChange={(files) => patchDraft({ ...composing, files })}
            />
          </EventForm>
        </Sheet>
      ) : null}

      {view === "today" ? (
        <Today
          events={todayEvents}
          tasks={todayTasks}
          loaded={loaded}
          today={today}
          onCompose={compose}
          spaceId={spaceId}
          self={self}
          opened={opened}
          onOpen={openSheet}
          onSave={saveEdit}
          onRemove={remove}
          goingLabel={goingLabel}
        />
      ) : view === "agenda" ? (
        <Agenda
          events={agendaEvents}
          loaded={loaded}
          today={today}
          onCompose={compose}
          spaceId={spaceId}
          self={self}
          opened={opened}
          onOpen={openSheet}
          onSave={saveEdit}
          onRemove={remove}
          goingLabel={goingLabel}
        />
      ) : view === "day" || view === "week" ? (
        <TimeGrid
          days={gridDays}
          anchor={selected}
          events={gridEvents}
          today={today}
          onAnchor={goToDate}
          zoom={zoom}
          onZoom={chooseZoom}
          onOpenDay={(date) => {
            goToDate(date);
            setView("day");
          }}
          onCompose={compose}
          spaceId={spaceId}
          self={self}
          opened={opened}
          onOpen={openSheet}
          onSave={saveEdit}
          goingLabel={goingLabel}
        />
      ) : (
        <Month
          events={monthEvents}
          month={month}
          selected={selected}
          today={today}
          onMonth={setMonth}
          zoom={zoom}
          onZoom={chooseZoom}
          onSelect={setSelected}
          onCompose={compose}
          spaceId={spaceId}
          self={self}
          opened={opened}
          onOpen={openSheet}
          onSave={saveEdit}
          onRemove={remove}
          goingLabel={goingLabel}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- agenda */

/**
 * Everything a row needs that is not the event itself.
 *
 * Handed down whole through the day sections rather than picked apart at each
 * level: the sheet a row opens needs the space and the person, and threading
 * two more props through three components by hand is how they get forgotten on
 * one of the two screens.
 */
interface RowHandlers {
  spaceId: string | null;
  self: string | null;
  /** The open sheet's `EventView.key`, or null when none is. */
  opened: string | null;
  onOpen: (key: string | null) => void;
  /**
   * Save an edit, at the scope the sheet asked for.
   *
   * Takes the **occurrence** rather than an id, because «только этот раз» is
   * about a particular day and only the occurrence knows which one it is.
   */
  onSave: (event: EventView, draft: EventDraft, scope: EditScope) => void;
  onRemove: (eventId: string) => void;
  /**
   * Whose event this is, in a word — or null when nobody has said they are
   * going.
   *
   * A shared calendar's first question about a row is «это чьё»: her dentist,
   * my meeting, or our dinner. The answer was on the sheet, behind a tap, on
   * every row of a list that is read far more often than it is opened.
   *
   * Passed as a function rather than baked into `EventView` because it is one
   * pass over the participants for the whole screen, not a lookup per row —
   * and because a repeating event is many rows sharing one answer.
   */
  goingLabel: (eventId: string) => string | null;
}

/**
 * What is coming, nearest first.
 *
 * An event counts as still ahead until the day it *ends* is over, so a trip you
 * are in the middle of does not disappear from the top of the screen on its
 * second morning.
 */
/**
 * What today is, in one place.
 *
 * Two things, because two things are what a household asks about a day: what
 * happens, and what is due. The second is the reason this view exists at all —
 * deadlines live on the lists screen, and «что сегодня надо сделать» was
 * otherwise answered by unfolding every list in turn.
 *
 * The events reuse the ordinary row, which already carries the place and the
 * note. The deadlines link into the list they belong to, at the row they are —
 * the same arrival the search hands out.
 */
function Today({
  events,
  tasks,
  loaded,
  today,
  onCompose,
  ...handlers
}: {
  events: EventView[];
  tasks: { item: ListItemView; list: ListView }[];
  loaded: boolean;
  today: Date;
  onCompose: (date: Date) => void;
} & RowHandlers) {
  const t = useT();
  const formats = useDateFormats();
  const { primary, secondary } = formatDayLabel(today, today);

  const nothing = events.length === 0 && tasks.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="flex items-baseline gap-2 px-1">
        <span className="font-display text-lg first-letter:uppercase">{primary}</span>
        <span className="text-[0.8125rem] text-muted">{secondary}</span>
      </h2>

      {nothing ? (
        <Card>
          {/* The same offer the empty agenda makes, for the same reason: a lone
              paragraph reads as a screen that failed rather than a free day. */}
          <div className="flex flex-col items-center gap-3 px-4 py-6">
            <p className="text-center text-[0.9375rem] text-muted">
              {loaded ? t("calendar.todayNothing") : t("calendar.loading")}
            </p>
            {loaded ? (
              <Button variant="secondary" onClick={() => onCompose(today)}>
                <PlusIcon className="size-[20px]" strokeWidth={1.9} />
                {t("calendar.newEvent")}
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}

      {tasks.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="px-1 text-[0.8125rem] font-semibold tracking-wide text-muted uppercase">
            {t("calendar.todayDue")}
          </h3>
          <ul className="flex flex-col gap-2">
            {tasks.map(({ item, list }) => {
              const late = new Date(item.dueAt!).getTime() < today.getTime();

              return (
                <li key={item.id}>
                  <Link
                    to={`/lists?list=${list.id}&item=${item.id}`}
                    className="flex items-center gap-3 rounded-row bg-surface px-3 py-2.5 shadow-soft transition-colors hover:bg-surface-alt"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block break-words">{item.text}</span>
                      <span className="mt-0.5 block text-[0.8125rem] text-muted">
                        {t("calendar.todayInList", { title: list.title })}
                      </span>
                    </span>
                    <span
                      className={cn(
                        "tabular shrink-0 text-[0.8125rem]",
                        late ? "text-neg" : "text-warn",
                      )}
                    >
                      {late
                        ? t("calendar.todayOverdue")
                        : formats.time.format(new Date(item.dueAt!))}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {events.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="px-1 text-[0.8125rem] font-semibold tracking-wide text-muted uppercase">
            {t("calendar.todayEvents")}
          </h3>
          <ul className="flex flex-col gap-2">
            {events.map((event) => (
              <EventRow key={event.key} event={event} {...handlers} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function Agenda({
  events,
  loaded,
  today,
  onCompose,
  ...handlers
}: {
  events: EventView[];
  loaded: boolean;
  today: Date;
  /** Only for the empty state — a row of its own opens the sheet, not this. */
  onCompose: (date: Date) => void;
} & RowHandlers) {
  const t = useT();
  const [showPast, setShowPast] = useState(false);

  const { upcoming, past } = useMemo(() => {
    const edge = today.getTime();
    const ahead: EventView[] = [];
    const behind: EventView[] = [];

    for (const event of events) {
      const ends = startOfDay(event.endsAt ?? event.startsAt).getTime();
      (ends >= edge ? ahead : behind).push(event);
    }

    return { upcoming: groupByDay(ahead), past: groupByDay(behind).reverse() };
  }, [events, today]);

  if (events.length === 0) {
    return (
      <Card>
        {/* Two different kinds of empty, and only one of them is a screen: a
            space with nothing in it yet, and a week that happens to be free.
            Both get the same offer, because in both the next useful thing is
            the same one — and a lone paragraph is what made this view feel
            broken when it last led. */}
        <div className="flex flex-col items-center gap-3 px-4 py-6">
          <p className="text-center text-[0.9375rem] text-muted">
            {loaded ? t("calendar.empty") : t("calendar.loading")}
          </p>
          {loaded ? (
            <Button variant="ghost" onClick={() => onCompose(today)}>
              <PlusIcon className="size-[20px]" strokeWidth={1.9} />
              {t("calendar.addOn", { date: formatDayShort(today) })}
            </Button>
          ) : null}
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {upcoming.length === 0 ? (
        <Card>
          {/* Not a dead end. A free week is good news, and the screen should
              say what to do with it rather than leave a paragraph sitting
              alone — which is what made this view feel broken when it was last
              the front page. */}
          <div className="flex flex-col items-center gap-3 px-4 py-6">
            <p className="text-center text-[0.9375rem] text-muted">
              {t("calendar.nothingAhead")}
            </p>
            <Button variant="ghost" onClick={() => onCompose(today)}>
              <PlusIcon className="size-[20px]" strokeWidth={1.9} />
              {t("calendar.addOn", { date: formatDayShort(today) })}
            </Button>
          </div>
        </Card>
      ) : null}

      {upcoming.map((group) => (
        <DaySection key={group.key} group={group} today={today} {...handlers} />
      ))}

      {past.length > 0 ? (
        <div className="pt-2">
          {showPast ? (
            <div className="flex flex-col gap-4">
              <h2 className="px-1 text-[0.8125rem] font-semibold tracking-wide text-muted uppercase">
                {t("calendar.pastHeading")}
              </h2>
              {past.map((group) => (
                <DaySection
                  key={group.key}
                  group={group}
                  today={today}
                  past
                  {...handlers}
                />
              ))}
            </div>
          ) : (
            <Button variant="ghost" full onClick={() => setShowPast(true)}>
              {t("calendar.showPast")}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function DaySection({
  group,
  today,
  past = false,
  ...handlers
}: { group: DayGroup; today: Date; past?: boolean } & RowHandlers) {
  const { primary, secondary } = formatDayLabel(group.date, today);

  return (
    <section className={cn("flex flex-col gap-2", past && "opacity-70")}>
      <h2 className="flex items-baseline gap-2 px-1">
        <span className="font-display text-lg first-letter:uppercase">{primary}</span>
        <span className="text-[0.8125rem] text-muted">{secondary}</span>
      </h2>
      <ul className="flex flex-col gap-2">
        {group.events.map((event) => (
          <EventRow key={event.key} event={event} {...handlers} />
        ))}
      </ul>
    </section>
  );
}

/**
 * One occurrence.
 *
 * The body opens the sheet — there is no pencil in the icon set, and on a phone
 * a whole tappable row beats hunting for a 20px target anyway. Deleting keeps
 * its own button so the two are never confused: the bin removes the whole
 * event, series and all, and that is a different thing from calling off one
 * day, which lives inside.
 */
function EventRow({
  event,
  spaceId,
  self,
  opened,
  onOpen,
  onSave,
  onRemove,
  goingLabel,
}: { event: EventView } & RowHandlers) {
  const t = useT();

  /*
   * The bin asks once before it takes an event away.
   *
   * A mis-tap here is not a mis-tap: `remove` deletes the whole series, so a
   * thumb landing wrong on a standing arrangement loses every occurrence of it,
   * and nothing on this screen puts one back. A dialog would be the heavier
   * answer and a worse one on a phone — this is the same gesture the wishes use,
   * and the question takes itself back, so a row left half-pressed is not still
   * armed when the screen comes back.
   */
  const [confirming, setConfirming] = useState(false);
  const removeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(false), CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [confirming]);

  return (
    // A row, not a card: this is one line in a list, and giving each event the
    // 22px radius of a card made a stack of them read as a stack of screens.
    <li
      className="flex items-center rounded-row bg-surface shadow-soft"
      /* Anything else pressed on this row takes the question back. Captured on
         pointerdown so it lands before the thing being pressed acts — the bin
         itself is excluded, or the second press would cancel what it came to
         confirm. */
      onPointerDownCapture={(pointer) => {
        if (!confirming) return;
        if (removeRef.current?.contains(pointer.target as Node)) return;
        setConfirming(false);
      }}
    >
      <button
        type="button"
        onClick={() => onOpen(event.key)}
        aria-label={t("calendar.detailsOf", { title: event.title })}
        className="group flex min-w-0 flex-1 items-start gap-3 rounded-row px-3 py-2.5 text-left transition-colors hover:bg-surface-alt"
      >
        <DatePlate date={event.startsAt} />

        <span className="min-w-0 flex-1">
          <span className="block break-words">{event.title}</span>

          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.8125rem]">
            <span className="tabular text-muted">{formatEventTime(event)}</span>
            {/* Whose it is, which is the first thing asked of a row in a shared
                calendar. Beside the time rather than in a corner: it is read in
                the same glance, and a badge floating on its own would be a
                second thing to look at. */}
            {/* The one labelled thing on the line, and brighter than the time
                beside it. It was a grey pill next to another grey pill of the
                same size, and «Вы» simply disappeared into «повторяется» — two
                chips reading as one piece of furniture. */}
            {goingLabel(event.id) ? (
              <span className="rounded-pill bg-surface-alt px-2 py-0.5 text-[0.75rem] font-medium text-ink transition-colors group-hover:bg-surface">
                {goingLabel(event.id)}
              </span>
            ) : null}
            {event.repeating ? (
              /*
               * The icon alone, with the word in its label.
               *
               * On every occurrence and not only the first: it answers «почему
               * это здесь», and the fifth Tuesday needs that answer as much as
               * the first. But «повторяется» beside a repeat arrow is the icon
               * said twice, and the two words were the wider half of a pair of
               * chips that made the one that matters unreadable.
               */
              <RepeatIcon
                className="size-[15px] shrink-0 text-muted"
                strokeWidth={1.8}
                aria-label={t("calendar.repeats")}
                role="img"
              />
            ) : null}
          </span>

          {event.location ? (
            <span className="mt-0.5 block text-[0.8125rem] text-muted">
              {event.location}
            </span>
          ) : null}
          {event.note ? (
            <span className="mt-1 block text-[0.8125rem] text-muted">{event.note}</span>
          ) : null}
        </span>
      </button>

      <button
        ref={removeRef}
        type="button"
        onClick={() => (confirming ? onRemove(event.id) : setConfirming(true))}
        aria-label={t(confirming ? "calendar.removeConfirm" : "calendar.removeEvent", {
          title: event.title,
        })}
        className={cn(
          "mr-1.5 inline-flex min-h-[var(--touch)] shrink-0 items-center justify-center gap-1.5",
          "rounded-pill transition-colors",
          confirming
            ? "bg-neg-soft px-3 text-[0.8125rem] font-semibold text-neg"
            : "size-[var(--touch)] text-muted/60 hover:bg-neg-soft hover:text-neg",
        )}
      >
        <TrashIcon className="size-[18px] shrink-0" strokeWidth={1.8} />
        {confirming ? t("common.confirmDelete") : null}
      </button>

      {/* By key normally, and by id for somebody who was sent here: a search
          hit knows the event, not the Tuesday. Safe because this only happens
          on the day view, where one day holds one occurrence. */}
      {opened === event.key || opened === event.id ? (
        <EventSheet
          event={event}
          spaceId={spaceId}
          self={self}
          onSave={(draft, scope) => {
            onSave(event, draft, scope);
            onOpen(null);
          }}
          onClose={() => onOpen(null)}
        />
      ) : null}
    </li>
  );
}

/* ------------------------------------------------------------------ sheet */

/**
 * Everything an event is, on a sheet that slides up over the screen.
 *
 * The row is a title, a time and a bin, and the server has taken files and a
 * discussion on an event since the phase they were built — with nowhere on this
 * screen to reach either. The same is true of calling off one day of a repeat:
 * the rule has always had room for it (`EXDATE`), and the only gesture that
 * removed a Friday was deleting every Friday.
 *
 * A sheet rather than a taller row, and rather than the inline form this
 * replaces. Three reasons:
 *
 *  - The row is a line in a list. The month view stacks it under a grid that
 *    already fills a phone screen, and a row that unfolds into a form pushed
 *    the grid — the thing being read — off the top under a thumb.
 *  - Files and a discussion are not fields. They act at once, and they need
 *    width; a strip of 84px tiles inside a list row is a row that changes
 *    height when a photo lands.
 *  - It is what a task already does (`ItemSheet`). The same gesture opening the
 *    same shape on a task and on an event is one thing to learn, not two.
 *
 * Its ceiling and its bottom padding come from `app/chrome`, so it stops short
 * of the floating tab bar rather than of a number typed here that drifts the
 * next time the bar changes.
 *
 * **Reading and editing are two states of this one sheet, not two sheets.**
 * Opening it used to land straight in the form, so somebody checking what time
 * dinner is was standing in every field of the event with a thumb. The pencil
 * is now the only door into the form, and it opens in place — a second window
 * over the first would put the files and the roll call behind glass while the
 * title is being fixed.
 */
function EventSheet({
  event,
  spaceId,
  self,
  onSave,
  onClose,
}: {
  event: EventView;
  spaceId: string | null;
  self: string | null;
  onSave: (draft: EventDraft, scope: EditScope) => void;
  onClose: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const chat = useChat(spaceId, self, null);

  /*
   * The draft is this sheet's, and it is not written back until «Готово».
   * Unlike a task — where a note is committed on blur — an event has a date and
   * a repeat rule on the same form, and a half-typed date committing itself
   * would move the event somewhere nobody chose.
   *
   * It is born at the pencil, out of the event as it stands. Two ways back to
   * the reading view, and they are deliberately not the same gesture:
   *
   *  - «Отмена» throws it away. That is what makes the next press of the pencil
   *    show the saved event rather than yesterday's half-typed one.
   *  - Escape only steps back. A stray key is a slip, not a decision, and
   *    losing a paragraph of note to one of them is not a trade anybody made.
   *
   * Either way it dies with the sheet: nothing typed here survives closing.
   */
  const [draft, setDraft] = useState<EventDraft | null>(null);
  const [editing, setEditing] = useState(false);

  /**
   * The draft «Готово» has already accepted, waiting on «этот раз или всю
   * серию». Null whenever nothing is being asked — which is always, for an
   * event that happens once.
   *
   * A snapshot rather than a flag, so the answer is about the edit that was
   * submitted. Typing again takes the question back (see `onChange` below)
   * rather than leaving it standing over words it no longer describes.
   */
  const [asking, setAsking] = useState<EventDraft | null>(null);

  /* The form is in the scrolling body and «Готово» is in the header, so the
     button reaches its form by name rather than by being inside it. */
  const formId = `event-form-${event.id}`;

  const sheetRef = useRef<HTMLDivElement>(null);

  /*
   * What Escape means, read through a ref so the effect below can be set up
   * once, on the way in: re-running it would pull focus back out of whichever
   * field the person had just moved into.
   *
   * It means "back one step", not "close". A miss in the form gives back the
   * reading view with the typing still there to return to; only from the
   * reading view does it take the sheet away — and a pending «этот раз или всю
   * серию» is a step of its own, so Escape answers neither of the two.
   */
  const escapeRef = useRef<() => void>(() => {});
  escapeRef.current = asking
    ? () => setAsking(null)
    : editing
      ? () => setEditing(false)
      : onClose;

  useEffect(() => {
    // Focus lands on the sheet itself rather than on the first field: on a
    // phone, focusing a text box also throws the keyboard up over half of what
    // was just opened.
    sheetRef.current?.focus();

    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== "Escape") return;
      keyEvent.preventDefault();
      escapeRef.current();
    };

    // The page underneath stays where it was rather than scrolling away under
    // the sheet.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const discussion = chat.discussionFor({ type: "event", id: event.id });

  /**
   * Open the discussion about this event, starting it if nobody has — the same
   * gesture a wish and a task have, so a question about Saturday lands in the
   * one place conversations live rather than in a note nobody answers.
   *
   * Named after the event as it is *saved*, not as the form currently reads:
   * closing throws the draft away, and a thread called after a title that was
   * never kept would be a thread about nothing on the chat screen.
   */
  function discuss() {
    const id =
      discussion?.id ?? chat.createThread(event.title, { type: "event", id: event.id });
    if (!id) return;

    onClose();
    navigate(`/chat?topic=${id}`);
  }

  /*
   * Whether the day this row stands for can be called off.
   *
   * Only for a rule `shared` can read: an event carrying something outside the
   * subset shows once and has no second occurrence to cancel, and rewriting a
   * rule we cannot parse would be guessing at when somebody's event happens.
   */
  const rule = parseRecurrence(event.rrule);
  const canSkip = spaceId !== null && rule !== null;

  /** The days of this series somebody has called off, oldest first. */
  const skipped = rule
    ? [...rule.exceptions].sort((a, b) => a.getTime() - b.getTime())
    : [];

  return createPortal(
    <>
      {/* Black rather than a token: dimming has to read as shade in both
          themes, and every surface token flips between them. */}
      <div onClick={onClose} aria-hidden="true" className="fixed inset-0 z-40 bg-black/45" />

      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("calendar.detailsOf", { title: event.title })}
        tabIndex={-1}
        className={cn(
          "pad-safe-x fixed inset-x-0 z-40 mx-auto w-full max-w-2xl",
          "flex flex-col overflow-hidden rounded-t-card bg-surface shadow-soft outline-none",
        )}
        style={{
          /*
           * Above the keyboard, not under it.
           *
           * `bottom-0` is the bottom of the *layout* viewport, and on iOS that
           * does not move when a keyboard comes up — the visual viewport
           * shrinks instead, so a sheet pinned to the bottom had its last
           * fields and its buttons behind the keys. `--keyboard-inset` is that
           * measurement (see `app/keyboard`), and it is nought on a laptop and
           * on Android, where the layout viewport shrinks by itself.
           */
          bottom: "var(--keyboard-inset, 0px)",
          maxHeight: PANE_HEIGHT,
          paddingBottom: `calc(var(--safe-bottom) + ${TAB_BAR_MARGIN}px)`,
        }}
      >
        {/* Closing on the left, the state's own action on the right — the same
            two ends in both states, so the ✕ never moves under a thumb that
            has learned where it is. */}
        <header className="flex items-center gap-2 border-b border-line-soft px-3 py-3.5">
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <CloseIcon className="size-[20px]" strokeWidth={1.9} />
          </button>

          <h2 className="min-w-0 flex-1 truncate font-display text-[1.0625rem]">
            {t(editing ? "calendar.editTitle" : "calendar.details")}
          </h2>

          {editing && draft ? (
            /* Saving lives up here rather than under the last field: the form
               is long enough to scroll on a phone, and the header does not. */
            /* `key` is load-bearing — see the note beside the wish sheet's
               pencil. Without it React reuses this DOM node for the pencil
               below, and the click that pressed the pencil submits the form it
               just opened. */
            <button
              key="commit"
              type="submit"
              form={formId}
              disabled={!draft.title.trim()}
              className="min-h-[var(--touch)] shrink-0 rounded-pill px-3.5 text-[0.9375rem] font-semibold text-accent transition-colors hover:bg-accent-soft disabled:text-muted disabled:hover:bg-transparent"
            >
              {t("calendar.done")}
            </button>
          ) : (
            <button
              key="edit"
              type="button"
              onClick={() => {
                // `??`: a draft Escape stepped away from is picked back up
                // where it was left. «Отмена» is what clears it.
                setDraft(draft ?? draftFromEvent(event));
                setEditing(true);
              }}
              aria-label={t("calendar.editEvent")}
              title={t("calendar.editEvent")}
              className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
            >
              <EditIcon className="size-[19px]" strokeWidth={1.9} />
            </button>
          )}
        </header>

        {/* `min-h-0` so this is the part that shrinks and scrolls when the
            sheet hits its ceiling — without it a flex child refuses to go below
            its content height and the buttons are simply cut off. */}
        {/* `overflow-x-hidden` is not tidiness. CSS says that when one axis is
            `visible` and the other is not, the `visible` one computes to
            `auto` — so a pane that asked for vertical scrolling silently became
            a scroll container sideways too, and the two time fields below
            (`flex-1` with no `min-w-0`, holding a native `time` input whose
            width is locale-dependent) were just wide enough to overflow it.
            Editing an event could be dragged left and right; creating one
            could not, because that form's parent clips instead of scrolling.

            And it is the *same* element in both states, which is the point: it
            keeps its scroll position across a press of the pencil instead of
            snapping the sheet back to the top of an event somebody had
            scrolled halfway down. */}
        <div className="flex min-h-0 flex-col overflow-x-hidden overflow-y-auto overscroll-contain">
          {editing && draft ? (
            <EventForm
              id={formId}
              draft={draft}
              // «Готово» in the header is the only way to save, so the row at
              // the foot of the form holds «Отмена» alone.
              submitLabel={null}
              // The composer opened because somebody pressed «+» and the next
              // thing they do is type. This one opened over an event they were
              // reading; throwing the keyboard up over it is not the welcome.
              autoFocus={false}
              // One date field cannot say "until Thursday", so an event that
              // spans days says out loud that saving will shorten it rather than
              // moving its end somewhere the person did not look.
              notice={
                event.endsAt && !sameDay(event.startsAt, event.endsAt)
                  ? t("calendar.spansDaysNotice", { date: formatDayShort(event.endsAt) })
                  : undefined
              }
              onChange={(next) => {
                setDraft(next);
                // Changing anything takes a pending question back: it was asked
                // about the words that were on the form when «Готово» was
                // pressed, and it must not be answered about different ones.
                setAsking(null);
              }}
              /*
               * A repeating event asks before it saves; everything else just
               * saves, which is what «Готово» has always meant.
               *
               * The question is skipped when the *rule itself* was edited —
               * switching «каждую неделю» to «по будням» is a statement about
               * the series by definition, and «только этот раз» would answer it
               * by dropping the rule from the copy, which is not what anybody
               * asked for.
               */
              onSubmit={(next) =>
                canSkip && next.rrule === event.rrule
                  ? setAsking(next)
                  : onSave(next, "series")
              }
              onCancel={() => {
                setEditing(false);
                setDraft(null);
                setAsking(null);
              }}
            />
          ) : (
            <EventDetails event={event} />
          )}

          {/*
            Siblings of the swap, never children of it.

            Joining an event is not editing it, and neither is opening its
            files or its discussion — so they are on screen in both states. The
            reason they live *here* rather than inside each branch is narrower
            than that: React keeps this subtree across the swap because it stays
            at the same place among its siblings, and a `Participants` that
            remounted would blink its whole roll call every time the pencil was
            pressed.
          */}
          <div className="flex flex-col gap-3 px-4 pb-4">
            {/* Files live on the event itself, not in a thread about it: the
                tickets, a photo of the invitation, the route. The strip renders
                one line of text when there is nothing yet, so an event nobody
                attached anything to keeps the shape it had. */}
            <AttachmentStrip
              spaceId={spaceId}
              target={{ type: "event", id: event.id }}
              compact
            />

            <Participants spaceId={spaceId} eventId={event.id} self={self} />

            <button
              type="button"
              onClick={discuss}
              className="inline-flex min-h-[var(--touch)] w-fit items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] text-muted transition-colors hover:text-accent"
            >
              <ChatIcon className="size-[18px]" strokeWidth={1.9} />
              {discussion
                ? t("calendar.discussionCount", { count: discussion.messageCount })
                : t("calendar.discuss")}
            </button>

            {canSkip ? (
              <div className="flex flex-col gap-1">
                {/*
                 * Closing takes the sheet with it, and that is not tidiness.
                 * The rule has just changed under any open draft, which still
                 * carries the old one — pressing «Готово» after this would
                 * write the day straight back onto the calendar.
                 */}
                <button
                  type="button"
                  onClick={() => {
                    skipDay(spaceId, event);
                    onClose();
                  }}
                  className="inline-flex min-h-[var(--touch)] w-fit items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] text-muted transition-colors hover:text-neg"
                >
                  <CloseIcon className="size-[18px]" strokeWidth={1.9} />
                  {t("calendar.skipDay", { date: formatDayShort(event.startsAt) })}
                </button>
                <p className="px-2 text-[0.75rem] text-muted">{t("calendar.skipDayHint")}</p>

                {/*
                 * The days that are off, and the way back.
                 *
                 * A cancelled occurrence is not on any screen — that is what
                 * cancelling it did — so «вернуть» cannot live where the day
                 * was. It lives on the series, which is the only thing that
                 * still knows about it, and which is one tap away from any
                 * other occurrence.
                 *
                 * Drawing the missing days back into the grid and the agenda,
                 * dimmed, was the other candidate. It puts a row that is not
                 * happening back into the two views a phone has least room in,
                 * right after they were compacted — and a greyed row beside a
                 * real one is exactly the pair a thumb confuses.
                 */}
                {skipped.length > 0 ? (
                  <div className="mt-1 flex flex-col gap-1.5">
                    <p className="px-2 text-[0.75rem] text-muted">
                      {t("calendar.skippedHint")}
                    </p>
                    <ul className="flex list-none flex-wrap gap-1.5">
                      {skipped.map((day) => (
                        <li key={dayKey(day)}>
                          <button
                            type="button"
                            onClick={() => restoreDay(spaceId, event, day)}
                            aria-label={t("calendar.restoreDay", {
                              date: formatDayShort(day),
                            })}
                            className="inline-flex min-h-[var(--touch)] items-center gap-1.5 rounded-pill bg-surface-alt px-3.5 text-[0.8125rem] text-muted transition-colors hover:text-accent-ink"
                          >
                            <RefreshIcon className="size-[15px] shrink-0" strokeWidth={1.9} />
                            {formatDayShort(day)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {/*
          The question, and only while it is being asked.

          Outside the scrolling body so it cannot be scrolled away from under a
          thumb, and gone the rest of the time: this screen was just compacted,
          and a permanent «этот раз / вся серия» block would be a row of the
          sheet spent on a choice that matters for one press.
        */}
        {asking ? (
          <div className="flex shrink-0 flex-col gap-2 bg-surface-alt px-4 py-3">
            <p className="px-1 text-[0.8125rem] text-ink-soft">{t("calendar.scopeAsk")}</p>

            {/* Stacked rather than side by side: «Только этот раз, 25 авг.» and
                «Всю серию» are nothing like the same width, and two buttons of
                unequal weight in one row read as a control and its footnote. */}
            <button
              type="button"
              onClick={() => onSave(asking, "once")}
              className="min-h-[var(--touch)] rounded-row bg-accent px-3.5 text-[0.9375rem] font-semibold text-ground transition-colors hover:bg-accent-ink"
            >
              {t("calendar.scopeOnce", { date: formatDayShort(event.startsAt) })}
            </button>
            <button
              type="button"
              onClick={() => onSave(asking, "series")}
              className="min-h-[var(--touch)] rounded-row bg-surface px-3.5 text-[0.9375rem] font-medium text-ink transition-colors hover:bg-surface-alt"
            >
              {t("calendar.scopeSeries")}
            </button>
            {/* Escape does this too, and a phone has no Escape. */}
            <button
              type="button"
              onClick={() => setAsking(null)}
              className="min-h-[var(--touch)] rounded-pill text-[0.8125rem] text-muted transition-colors hover:text-ink"
            >
              {t("common.cancel")}
            </button>
          </div>
        ) : null}
      </div>
    </>,
    document.body,
  );
}

/**
 * The event, read rather than filled in.
 *
 * Nothing here is a box. That is the whole difference between this and the form
 * behind the pencil: a filled `surface-alt` rectangle is what a field looks
 * like in this language, so a reading view built out of them would be a form
 * with the typing disabled, and the split would have bought nothing.
 *
 * The repeat is a sentence for the same reason. «Частота / интервал / дни
 * недели» is three controls answering a question people ask in four words.
 */
function EventDetails({ event }: { event: EventView }) {
  const t = useT();
  const names = useWeekdayNames();

  const when = formatDayLabel(event.startsAt);
  const repeat = describeRepeat(event.rrule, t, names);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-1">
        <h3 className="font-display text-[1.375rem] leading-snug break-words">
          {event.title}
        </h3>
        <p className="text-[0.9375rem] text-ink-soft first-letter:uppercase">
          {when.primary} · {when.secondary}
        </p>
        <p className="tabular text-[0.9375rem] text-muted">{formatEventTime(event)}</p>
      </div>

      {repeat ? (
        <p className="flex items-center gap-1.5 text-[0.8125rem] text-muted">
          <RepeatIcon className="size-[15px] shrink-0" strokeWidth={1.8} />
          {repeat}
        </p>
      ) : null}

      {event.location ? <Detail label={t("calendar.location")} text={event.location} /> : null}
      {event.note ? <Detail label={t("calendar.fieldNote")} text={event.note} /> : null}

      {/* The link, when there is one — a call to join, a ticket, a map. It is
          the thing somebody reaches for in the minute before the event, which
          is exactly when this view is open.

          `rel="noreferrer"`: the destination has no business learning which
          page sent somebody, and `noopener` comes with it. */}
      {event.url ? (
        <a
          href={event.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-[var(--touch)] w-fit max-w-full items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] text-accent transition-colors hover:text-accent-ink"
        >
          <LinkIcon className="size-[18px] shrink-0" strokeWidth={1.9} />
          <span className="truncate">{event.url}</span>
        </a>
      ) : null}
    </div>
  );
}

/**
 * A named line of the event.
 *
 * Only where the words alone would not say what they are: a place and a note
 * are both a sentence in grey, and one of them is an address. The title, the
 * day and the time need no label — nobody has ever mistaken «19:00» for a note.
 */
function Detail({ label, text }: { label: string; text: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[0.75rem] font-semibold tracking-wide text-muted uppercase">
        {label}
      </span>
      <p className="text-[0.9375rem] break-words whitespace-pre-wrap">{text}</p>
    </div>
  );
}

/**
 * The repeat rule as a sentence, or null when the event happens once.
 *
 * The same read `RepeatField` performs — `readRepeat` is the single place that
 * decides which of the seven a stored rule is — said out loud instead of drawn
 * as a row of pills, so the two can never disagree about what an event does.
 */
function describeRepeat(
  rrule: string | null,
  t: ReturnType<typeof useT>,
  names: readonly string[],
): string | null {
  const { choice, days } = readRepeat(rrule);

  switch (choice) {
    case "none":
      return null;
    case "daily":
      return t("calendar.repeatDaily");
    case "weekdays":
      return t("calendar.repeatWeekdays");
    case "weekly":
      return t("calendar.repeatWeekly");
    case "monthly":
      return t("calendar.repeatMonthly");
    case "custom":
      // Already Monday-first out of `readRepeat`, and `names` is Monday-first
      // too — the same order the chips are drawn in.
      return t("calendar.repeatOnDays", {
        days: days.map((day) => names[WEEKDAYS.indexOf(day)] ?? day).join(", "),
      });
    case "own":
      return t("calendar.repeatOwn");
  }
}

/**
 * Call off one day of a repeat.
 *
 * The day joins the rule's `exceptions` and the event is saved. The string is
 * never spliced by hand: it is read with `parseRecurrence` and written with
 * `formatRecurrence`, so the `EXDATE` this leaves behind is exactly the one
 * `shared` expands against — which is what makes the day disappear from the
 * month grid and from «Ближайшее» at once, with no second code path.
 *
 * A **day**, not an instant, because that is what the rule matches on and what
 * a person means: move the stand-up to 11:00 later and the cancelled Friday
 * stays cancelled. See the comment over `Recurrence.exceptions`.
 *
 * The patch carries the rule and nothing else, which is why this goes straight
 * to the outbox instead of through `useCalendar.update`. `update` rebuilds the
 * whole event out of a draft, and the only draft this row can build describes
 * the *occurrence* — saving it would drag the series' own `startsAt` onto the
 * very day being cancelled and drop a multi-day end along the way. A
 * `cancelOccurrence` on `CalendarActions` is where this belongs; `app/calendar`
 * was closed for this round.
 */
function skipDay(spaceId: string, event: EventView): void {
  const rule = parseRecurrence(event.rrule);
  if (!rule) return;

  const day = startOfDay(event.startsAt);
  // Already off: writing it twice is a revision for a change nobody made, and
  // the sync layer would ship it.
  if (rule.exceptions.some((at) => sameDay(at, day))) return;

  syncEngine.mutate(
    operations.updateEvent(spaceId, event.id, {
      rrule: formatRecurrence({ ...rule, exceptions: [...rule.exceptions, day] }),
    }),
  );
}

/**
 * Put a called-off day back.
 *
 * The exact inverse of `skipDay`, and it has to exist: `EXDATE` was a one-way
 * door, so a thumb landing on «Не в этот раз» removed a Tuesday for good and
 * the only gesture left was deleting the series and building it again.
 *
 * The sheet stays open afterwards, unlike a skip. The occurrence it was opened
 * from is untouched by this — its key does not move — and the day coming back is
 * something to watch happen, not a reason to be thrown off the screen.
 */
function restoreDay(spaceId: string, event: EventView, day: Date): void {
  const rule = parseRecurrence(event.rrule);
  if (!rule) return;

  const kept = rule.exceptions.filter((at) => !sameDay(at, day));
  // Nothing matched: the day is already on. Writing the same rule back would be
  // a revision for a change nobody made, and the sync layer would ship it.
  if (kept.length === rule.exceptions.length) return;

  syncEngine.mutate(
    operations.updateEvent(spaceId, event.id, {
      rrule: formatRecurrence({ ...rule, exceptions: kept }),
    }),
  );
}

/* ---------------------------------------------------------- participants */

/** Идут, потом молчащие, потом отказавшиеся. */
const ANSWER_ORDER: Record<EventParticipantStatus, number> = {
  going: 0,
  unanswered: 1,
  not_going: 2,
};

/** How somebody else's answer reads, in the third person. */
const ANSWER_LABEL: Record<EventParticipantStatus, MessageKey> = {
  going: "calendar.participantGoing",
  not_going: "calendar.participantNotGoing",
  unanswered: "calendar.participantUnanswered",
};

/**
 * The two you can press. «Пока не знаю» is not one of them: pressing the lit
 * answer again is that third state, and two pills side by side fit a phone
 * where three do not.
 */
const ANSWERS: readonly { status: EventParticipantStatus; key: MessageKey }[] = [
  { status: "going", key: "calendar.participateGoing" },
  { status: "not_going", key: "calendar.participateNotGoing" },
];

/**
 * Кто идёт (SPEC §4.4).
 *
 * Everyone in the space is on the list, answered or not — «кто идёт» is only a
 * useful question if the person who has said nothing is visible as such. The
 * hook folds a missing row into `unanswered`, so nothing here can tell silence
 * from an absent row, and nothing here needs to.
 *
 * Only your own answer has controls on it. That is the shape of the whole
 * feature, all the way down to a route that takes no user (`…/participants/me`)
 * — there is no gesture on this screen through which one member answers for the
 * other.
 *
 * The answer belongs to the **event**, not to the occurrence: a weekly stand-up
 * is one stored row, so «иду» said on one Friday is said for the series. Answers
 * per occurrence would need a key per occurrence on the server; the sheet does
 * not pretend to offer them.
 */
function Participants({
  spaceId,
  eventId,
  self,
}: {
  spaceId: string | null;
  eventId: string;
  self: string | null;
}) {
  const t = useT();
  const { participants, mine, goingCount, setMine } = useEventParticipants(
    spaceId,
    eventId,
    self,
  );

  const ordered = useMemo(
    () =>
      // `sort` is stable, so within one answer people keep the order the space
      // lists them in rather than shuffling as somebody answers.
      [...participants].sort((a, b) => ANSWER_ORDER[a.status] - ANSWER_ORDER[b.status]),
    [participants],
  );

  /*
   * Alone in the space — a new one, or one whose invitation has not been taken
   * up yet. The list would be a single row repeating what the switch above it
   * already says, and the tally would count to one; the switch alone is the
   * whole block.
   */
  const alone = ordered.length < 2;
  const answered = ordered.some((person) => person.status !== "unanswered");

  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2 px-1">
        <h3 className="text-[0.8125rem] font-medium text-ink-soft">
          {t("calendar.participants")}
        </h3>
        {alone ? null : (
          // «Пока никто не ответил» only while that is true: once somebody has
          // said no, a count of zero is the honest summary and the other line
          // would be a lie about a screen the person is looking at.
          <span className="shrink-0 whitespace-nowrap text-[0.8125rem] text-muted">
            {goingCount > 0 || answered
              ? t("calendar.participantsGoing", { count: goingCount })
              : t("calendar.participantsNobody")}
          </span>
        )}
      </div>

      <div
        role="group"
        aria-label={t("calendar.participateLabel")}
        className="flex gap-1.5"
      >
        {ANSWERS.map(({ status, key }) => {
          const chosen = mine === status;

          return (
            <button
              key={status}
              type="button"
              aria-pressed={chosen}
              // Pressing the lit answer takes it back off. Without it «Иду» is a
              // door that only opens one way: there would be no gesture for
              // «уже не знаю» short of declining, which says something else.
              onClick={() => setMine(chosen ? "unanswered" : status)}
              className={cn(
                "min-h-[var(--touch)] flex-1 rounded-pill px-3.5 text-[0.9375rem] transition-colors",
                chosen
                  ? "bg-accent-soft font-semibold text-accent-ink"
                  : "bg-surface-alt font-medium text-muted hover:text-ink-soft",
              )}
            >
              {t(key)}
            </button>
          );
        })}
      </div>

      {alone ? null : (
        <ul className="mt-0.5 flex flex-col">
          {ordered.map((person) => (
            <li
              key={person.userId}
              className="flex min-h-[var(--touch)] items-center gap-2.5 px-1"
            >
              <Face name={person.name} src={person.avatarUrl} />

              <span className="min-w-0 flex-1">
                {/* «Вы» rather than your own name, the way the chat names your
                    own messages — and one word cannot push the answer off a
                    narrow row. */}
                <span className="block truncate text-[0.9375rem]">
                  {person.isSelf ? t("common.you") : person.name}
                </span>
                {person.isCreator ? (
                  <span className="block truncate text-[0.75rem] text-muted">
                    {t("calendar.participantCreator")}
                  </span>
                ) : null}
              </span>

              <span
                className={cn(
                  "flex shrink-0 items-center gap-1 whitespace-nowrap text-[0.8125rem]",
                  person.status === "going" ? "text-accent-ink" : "text-muted",
                )}
              >
                {person.status === "going" ? (
                  <CheckIcon className="size-[15px]" strokeWidth={2.2} />
                ) : null}
                {t(ANSWER_LABEL[person.status])}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * A person as a circle: their photo, or the initials most people never replace.
 *
 * `src` is always a path on our own origin (`/api/media/…`), minted by the
 * server. Smaller than the member list's circle in settings — this one sits in a
 * sheet that also has to hold a form.
 */
function Face({ name, src }: { name: string; src: string | null }) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="size-8 shrink-0 rounded-pill bg-accent-soft object-cover"
      />
    );
  }

  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <span
      aria-hidden="true"
      className="grid size-8 shrink-0 place-items-center rounded-pill bg-accent-soft font-display text-[0.8125rem] text-accent-ink"
    >
      {initials}
    </span>
  );
}

/**
 * The round date plate that opens a row: 40px of `surface-alt` carrying the day
 * number over its weekday.
 *
 * It is `aria-hidden` on purpose. The heading above the group already says
 * which day this is, and the row's own label is the event title — a screen
 * reader hearing "25 пн" before every title would be worse off, not better.
 *
 * The hover swap is not decoration: the row's hover state *is* `surface-alt`,
 * so a plate that stayed there would vanish under the pointer. It steps the
 * other way instead and keeps its tonal distance either way.
 */
function DatePlate({ date }: { date: Date }) {
  const formats = useDateFormats();

  return (
    <span
      aria-hidden="true"
      className="grid size-10 shrink-0 place-content-center justify-items-center rounded-pill bg-surface-alt leading-none transition-colors group-hover:bg-surface"
    >
      <span className="tabular text-[0.9375rem] font-semibold">{date.getDate()}</span>
      <span className="mt-1 text-[0.625rem] text-muted">
        {formats.weekdayShort.format(date)}
      </span>
    </span>
  );
}

/* ----------------------------------------------------------------- month */

/**
 * The month grid.
 *
 * Weeks start on Monday, and the cells are laid out edge to edge so that each
 * one clears the 44px touch floor on a 360px screen — a grid with gutters at
 * that width gives 41px cells, and people miss them.
 */
function Month({
  events,
  month,
  selected,
  today,
  onMonth,
  zoom,
  onZoom,
  onSelect,
  onCompose,
  ...handlers
}: {
  events: EventView[];
  month: Date;
  selected: Date;
  today: Date;
  onMonth: (month: Date) => void;
  zoom: Exclude<View, "agenda">;
  onZoom: (next: Exclude<View, "agenda">) => void;
  onSelect: (date: Date) => void;
  onCompose: (date: Date) => void;
} & RowHandlers) {
  const t = useT();
  const weekdays = useWeekdayNames();
  const cells = useMemo(() => monthGrid(month), [month]);
  const onSelectedDay = eventsOnDay(events, selected);
  const label = formatDayLabel(selected, today);
  const onThisMonth = sameMonth(month, today);

  /**
   * Paging the month moves the selection with it.
   *
   * Otherwise the panel underneath keeps describing a day that is no longer on
   * the grid, which reads as the screen having failed to update.
   */
  function goTo(next: Date) {
    onMonth(next);
    onSelect(sameMonth(next, today) ? today : startOfMonth(next));
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Two rows, because one did not fit. The period gets the whole of the
          first; the arrows flank it and everything else moved below. */}
      <div className="flex flex-col gap-2 px-1">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => goTo(addMonths(month, -1))}
            aria-label={t("calendar.prevMonth")}
            className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <ChevronLeftIcon className="size-[20px]" strokeWidth={1.9} />
          </button>

          <h2 className="min-w-0 flex-1 truncate text-center font-display text-lg first-letter:uppercase">
            {formatMonthTitle(month)}
          </h2>

          <button
            type="button"
            onClick={() => goTo(addMonths(month, 1))}
            aria-label={t("calendar.nextMonth")}
            className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <ChevronRightIcon className="size-[20px]" strokeWidth={1.9} />
          </button>
        </div>

        <div className="flex items-center gap-1">
          <ZoomSwitch zoom={zoom} onZoom={onZoom} />
          {onThisMonth ? null : (
            <button
              type="button"
              onClick={() => goTo(startOfMonth(today))}
              className="min-h-[var(--touch)] shrink-0 rounded-pill px-3.5 text-[0.8125rem] font-medium text-accent transition-colors hover:bg-accent-soft"
            >
              {t("calendar.today")}
            </button>
          )}
        </div>
      </div>

      {/* The grid is a surface, and nothing inside it is drawn. The cells used
          to be separated by a `gap-px` that read as ruling at a glance; what
          separates a day from its neighbour now is the space around the circle
          and nothing else. */}
      <Card className="px-2 py-3">
        <div
          aria-hidden="true"
          className="grid grid-cols-7 pb-2 text-center text-[0.6875rem] font-medium text-muted"
        >
          {weekdays.map((name) => (
            <span key={name}>{name}</span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-y-1">
          {cells.map((date) => (
            <DayCell
              key={dayKey(date)}
              date={date}
              inMonth={date.getMonth() === month.getMonth()}
              isToday={sameDay(date, today)}
              isSelected={sameDay(date, selected)}
              count={eventsOnDay(events, date).length}
              onSelect={() => onSelect(startOfDay(date))}
            />
          ))}
        </div>
      </Card>

      <section className="flex flex-col gap-2">
        <h3 className="flex items-baseline gap-2 px-1">
          <span className="font-display text-lg first-letter:uppercase">
            {label.primary}
          </span>
          <span className="text-[0.8125rem] text-muted">{label.secondary}</span>
        </h3>

        {onSelectedDay.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-3 px-4 py-6">
              <p className="text-center text-[0.9375rem] text-muted">
                {t("calendar.emptyDay")}
              </p>
              <Button variant="ghost" onClick={() => onCompose(selected)}>
                <PlusIcon className="size-[20px]" strokeWidth={1.9} />
                {t("calendar.addOn", { date: formatDayShort(selected) })}
              </Button>
            </div>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {onSelectedDay.map((event) => (
              <EventRow key={event.key} event={event} {...handlers} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function DayCell({
  date,
  inMonth,
  isToday,
  isSelected,
  count,
  onSelect,
}: {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  count: number;
  onSelect: () => void;
}) {
  const t = useT();
  const day = formatDayShort(date);

  return (
    /*
     * A circle, and the whole cell is it.
     *
     * `aspect-square` off the column width keeps the shape honest at any screen
     * size; the cap stops it from becoming a coin on a tablet, and at the 360px
     * phone the design targets a column is 44.6px, which clears the touch floor
     * on its own.
     */
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={count > 0 ? t("calendar.dayWithEvents", { date: day }) : day}
      className={cn(
        "relative mx-auto grid aspect-square w-full max-w-[46px] place-items-center rounded-pill",
        "tabular text-[0.9375rem] transition-colors",
        // Not a state so much as an absence of one: these days belong to the
        // month either side and are there to keep the columns honest. Dimmed
        // only while nothing else is claiming the cell — paging to September
        // puts a 31 August that is still *today* in the first row, and muting
        // it on top of the amber fill would leave it unreadable.
        !inMonth && !isToday && !isSelected && "text-muted/45",
        isToday
          ? "bg-accent font-semibold text-ground"
          : isSelected
            ? "bg-accent-soft font-semibold text-accent-ink"
            : "hover:bg-surface-alt",
      )}
    >
      <span>{date.getDate()}</span>
      {/* One dot, not a count: at this size three of them were a texture, and
          nobody was reading them as "two events". The dot says something is on
          that day and the row below says what. */}
      {count > 0 ? (
        <span
          aria-hidden="true"
          className={cn(
            "absolute bottom-[6px] size-[5px] rounded-pill",
            // On today's filled circle the accent is the background, so the dot
            // has to be the ground to exist at all.
            isToday ? "bg-ground" : "bg-accent",
          )}
        />
      ) : null}
    </button>
  );
}

/* --------------------------------------------------------- week and day */

/**
 * Неделя и день: сетка, в которой час — это высота.
 *
 * «Ближайшее» and «Месяц» both answer *which day*. Neither can answer *when on
 * that day*, and neither can show that two things are at the same time — a
 * 19:00 concert and a 19:30 call are two rows, one under the other, looking for
 * all the world like an evening with room in it. That is what this is for.
 *
 * One component, drawn once with seven columns and once with one. The week and
 * the day are not two designs: they are the same scale at two widths, and
 * writing them twice is how they drift.
 *
 * Three decisions worth stating, because each had another plausible answer:
 *
 *  - **A day column is clipped at midnight, not at the event.** An occurrence
 *    that runs 22:00 → 01:00 is drawn on both days, from ten to twelve on the
 *    first and from twelve to one on the second. The alternative — sending
 *    anything that crosses midnight up to the all-day strip, which is what most
 *    calendars do — takes the one late evening a couple actually plans around
 *    and removes exactly the thing this view exists to show. Each block still
 *    spells out the event's real times, so a clipped block never claims to end
 *    at midnight.
 *  - **What would fill a column goes to the strip instead.** All-day events
 *    have no time to place; a segment covering 00:00 to 24:00 has a time but
 *    nowhere to put it, and stretching it down the whole column would bury
 *    every meeting behind it. The middle days of a three-day trip land there.
 *  - **An event with no end is drawn for half an hour.** It has no duration to
 *    show, and half an hour is the smallest thing this grid can draw — the
 *    least it can assert while still being a block you can hit.
 */

/** An hour, in pixels. A half-hour is 26px; three hours is 156px. */
const HOUR_HEIGHT = 52;
const MINUTE_HEIGHT = HOUR_HEIGHT / 60;
const DAY_MINUTES = 1440;
const MINUTE_MS = 60_000;

/** The hour column down the left, wide enough for «08:00» at 10px. */
const GUTTER = 44;

/** Nothing is drawn shorter: below this the title has nowhere to go. */
const MIN_BLOCK = 26;

/** Where the scroll opens, and where a keyboard-pressed empty column lands. */
const WORKDAY_START_HOUR = 8;

/** How long an event with no end is drawn — and taken to occupy, for overlap. */
const NO_END_MINUTES = 30;

/** A tap on empty space rounds to the nearest of these. */
const SLOT_MINUTES = 30;

/** 1…23. Midnight has no line and no label: it is the edge of the grid. */
const HOURS: readonly number[] = Array.from({ length: 23 }, (_, i) => i + 1);

/** The Monday of the week a date falls in. Monday-first, like the month grid. */
function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() + 6) % 7));
}

/**
 * One event as it is drawn on one day: minutes from that midnight, and the
 * share of the column's width it was given.
 */
interface Block {
  event: EventView;
  /** Clipped to the column: 0 is its midnight, 1440 the next. */
  from: number;
  to: number;
  /** Which of `columns` side-by-side lanes this one took. */
  column: number;
  columns: number;
}

/**
 * Split one day into what the strip holds and what the scale holds, and give
 * everything on the scale a lane.
 *
 * **Overlaps share the width.** The events are walked in clock order and cut
 * into clusters — a cluster ends the moment an event starts after everything
 * before it has finished. Inside one cluster each event takes the first lane
 * whose previous event has already ended, and every event in that cluster is
 * then drawn at one over the number of lanes the cluster needed. So two
 * overlapping events are two halves of the column, three are three thirds, and
 * an event that overlaps neither of its neighbours keeps the full width.
 *
 * Per cluster rather than per day, which is the point: a busy morning must not
 * make a lone evening event a quarter of the column wide.
 */
function dayLayout(
  events: readonly EventView[],
  day: Date,
): { band: EventView[]; blocks: Block[] } {
  const from = day.getTime();
  const to = addDays(day, 1).getTime();

  const band: EventView[] = [];
  const clipped: { event: EventView; from: number; to: number }[] = [];

  for (const event of events) {
    if (event.allDay) {
      // By the day it covers rather than by the instant: an all-day event
      // carries whatever time it was given, and that time means nothing.
      if (coversDay(event, day)) band.push(event);
      continue;
    }

    const starts = event.startsAt.getTime();
    const ends = event.endsAt
      ? Math.max(event.endsAt.getTime(), starts)
      : starts + NO_END_MINUTES * MINUTE_MS;

    // Touching the edge is not overlapping it: something that ends at midnight
    // does not belong to the next day.
    if (starts >= to || ends <= from) continue;

    if (starts <= from && ends >= to) {
      band.push(event);
      continue;
    }

    clipped.push({
      event,
      from: Math.max(0, (starts - from) / MINUTE_MS),
      to: Math.min(DAY_MINUTES, (ends - from) / MINUTE_MS),
    });
  }

  band.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());

  // Longest first among equal starts, so the event that shapes the cluster
  // takes the leftmost lane and the short ones stack beside it.
  clipped.sort((a, b) => a.from - b.from || b.to - b.from - (a.to - a.from));

  const blocks: Block[] = [];
  let cluster: typeof clipped = [];
  /** The last minute anything in the open cluster runs to. */
  let reach = -1;

  function flush() {
    if (cluster.length === 0) return;

    /** Where each lane's last event ended. */
    const lanes: number[] = [];
    const placed = cluster.map((seg) => {
      // `<=` and not `<`: 10:00–11:00 followed by 11:00–12:00 is not an
      // overlap, and putting them side by side would say it was.
      let column = lanes.findIndex((end) => end <= seg.from);
      if (column === -1) column = lanes.length;
      lanes[column] = seg.to;
      return { seg, column };
    });

    for (const { seg, column } of placed) {
      blocks.push({ ...seg, column, columns: lanes.length });
    }

    cluster = [];
    reach = -1;
  }

  for (const seg of clipped) {
    if (cluster.length > 0 && seg.from >= reach) flush();
    cluster.push(seg);
    reach = Math.max(reach, seg.to);
  }
  flush();

  return { band, blocks };
}

/** One all-day entry, over the run of days it covers. */
interface BandEntry {
  event: EventView;
  /** Column indices, inclusive. */
  start: number;
  end: number;
  row: number;
}

/**
 * The strip above the scale: one bar per event, spanning the days it covers.
 *
 * A trip is one bar across four columns rather than four chips saying the same
 * word — which is the whole reason the strip is a grid and not seven stacks.
 * Bars that share a day are stacked into rows by the same first-free-lane walk
 * the columns use, one dimension over.
 */
function bandRows(perDay: readonly EventView[][]): { rows: number; entries: BandEntry[] } {
  const spans = new Map<string, { event: EventView; start: number; end: number }>();

  perDay.forEach((events, index) => {
    for (const event of events) {
      const span = spans.get(event.key);
      // The run is contiguous by construction — a day an event covers cannot
      // sit between two it does not — so the last day seen is the end.
      if (span) span.end = index;
      else spans.set(event.key, { event, start: index, end: index });
    }
  });

  const ordered = [...spans.values()].sort(
    (a, b) => a.start - b.start || b.end - b.start - (a.end - a.start),
  );

  const rowEnd: number[] = [];
  const entries = ordered.map((span) => {
    let row = rowEnd.findIndex((end) => end < span.start);
    if (row === -1) row = rowEnd.length;
    rowEnd[row] = span.end;
    return { ...span, row };
  });

  return { rows: rowEnd.length, entries };
}

function TimeGrid({
  days,
  anchor,
  events,
  today,
  onAnchor,
  zoom,
  onZoom,
  onOpenDay,
  onCompose,
  spaceId,
  self,
  opened,
  onOpen,
  onSave,
}: {
  /** One day, or seven. Nothing else is drawn differently. */
  days: Date[];
  /** The day the two arrows step from, and the one lit in a week. */
  anchor: Date;
  events: EventView[];
  today: Date;
  onAnchor: (date: Date) => void;
  zoom: Exclude<View, "agenda">;
  onZoom: (next: Exclude<View, "agenda">) => void;
  onOpenDay: (date: Date) => void;
  onCompose: (date: Date, time: string) => void;
  /*
   * No bin here, unlike a row. A block is forty pixels of column and a delete
   * target inside one would be pressed by accident; the gesture stays where
   * there is room for it to be deliberate — the list and the month panel.
   */
} & Omit<RowHandlers, "onRemove">) {
  const t = useT();
  const formats = useDateFormats();
  const weekdays = useWeekdayNames();
  const scroller = useRef<HTMLDivElement>(null);

  /*
   * The clock, re-read every minute, so the «сейчас» line walks down the day
   * instead of freezing wherever the screen happened to open.
   */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), MINUTE_MS);
    return () => window.clearInterval(id);
  }, []);

  const span = days.length;
  const week = span === 7;

  const columns = useMemo(() => days.map((day) => dayLayout(events, day)), [events, days]);
  const band = useMemo(() => bandRows(columns.map((column) => column.band)), [columns]);

  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) return;

    /*
     * Not at midnight. A grid that opens on the top of the scale opens on eight
     * hours of nothing, and every visit starts with the same scroll.
     *
     * Mount only, and deliberately: paging to next week must not yank the
     * scroll back to the morning under somebody's thumb.
     */
    const at = new Date();
    const showsToday = days.some((day) => sameDay(day, at));
    const hour = showsToday
      ? Math.max(WORKDAY_START_HOUR, at.getHours() - 1)
      : WORKDAY_START_HOUR;

    element.scrollTop = hour * HOUR_HEIGHT;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const first = days[0] ?? today;
  const last = days[span - 1] ?? today;
  const showsToday = days.some((day) => sameDay(day, today));
  const label = formatDayLabel(first, today);

  /** Where a tap landed on an empty column, to the nearest half hour. */
  function composeAt(day: Date, press: ReactMouseEvent<HTMLButtonElement>) {
    const box = press.currentTarget.getBoundingClientRect();

    // `detail === 0` is a keyboard press: there is no pointer to read a time
    // off, so it lands on the working start of the day rather than on midnight.
    const minutes =
      press.detail === 0
        ? WORKDAY_START_HOUR * 60
        : (press.clientY - box.top) / MINUTE_HEIGHT;

    const snapped = Math.min(
      DAY_MINUTES - SLOT_MINUTES,
      Math.max(0, Math.round(minutes / SLOT_MINUTES) * SLOT_MINUTES),
    );

    onCompose(day, timeValue(new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, snapped)));
  }

  const openedEvent = opened ? (events.find((event) => event.key === opened) ?? null) : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Two rows — see the note on `ZoomSwitch` for what was wrong with one. */}
      <div className="flex flex-col gap-2 px-1">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onAnchor(addDays(anchor, -span))}
            aria-label={t(week ? "calendar.prevWeek" : "calendar.prevDay")}
            className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <ChevronLeftIcon className="size-[20px]" strokeWidth={1.9} />
          </button>

          <h2 className="flex flex-1 items-baseline justify-center gap-2 overflow-hidden">
            {week ? (
              // Only the dates, joined by a dash — the words either side of a
              // range are the kind of thing Intl gets right per language and a
              // hand-built sentence gets wrong in one of them.
              <span className="tabular truncate font-display text-lg">
                {`${formats.dayMonthShort.format(first)} – ${formats.dayMonthShort.format(last)}`}
              </span>
            ) : (
              <>
                <span className="truncate font-display text-lg first-letter:uppercase">
                  {label.primary}
                </span>
                <span className="shrink-0 text-[0.8125rem] text-muted">
                  {label.secondary}
                </span>
              </>
            )}
          </h2>

          <button
            type="button"
            onClick={() => onAnchor(addDays(anchor, span))}
            aria-label={t(week ? "calendar.nextWeek" : "calendar.nextDay")}
            className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <ChevronRightIcon className="size-[20px]" strokeWidth={1.9} />
          </button>
        </div>

        <div className="flex items-center gap-1">
          <ZoomSwitch zoom={zoom} onZoom={onZoom} />
          {showsToday ? null : (
            <button
              type="button"
              onClick={() => onAnchor(today)}
              className="min-h-[var(--touch)] shrink-0 rounded-pill px-3.5 text-[0.8125rem] font-medium text-accent transition-colors hover:bg-accent-soft"
            >
              {t("calendar.today")}
            </button>
          )}
        </div>
      </div>

      <Card>
        {/* The days themselves, and the all-day strip, sit outside the scroll:
            they are what the columns below *are*, and a heading that scrolls
            away leaves an unlabelled grid. */}
        <div className="flex border-b border-line">
          <span aria-hidden="true" className="shrink-0" style={{ width: GUTTER }} />
          <div className="grid flex-1" style={{ gridTemplateColumns: track(span) }}>
            {days.map((day, index) => {
              const heading = (
                <>
                  <span className="text-[0.6875rem] text-muted">
                    {/* Monday-first and seven of them, which is exactly what the
                        hook returns — the same names the month grid heads its
                        columns with. */}
                    {week ? weekdays[index] : formats.weekdayShort.format(day)}
                  </span>
                  <span
                    className={cn(
                      "tabular grid size-7 place-items-center rounded-pill text-[0.875rem]",
                      sameDay(day, today)
                        ? "bg-accent font-semibold text-ground"
                        : week && sameDay(day, anchor)
                          ? "bg-accent-soft font-semibold text-accent-ink"
                          : null,
                    )}
                  >
                    {day.getDate()}
                  </span>
                </>
              );

              // On a week, the heading is the way into that one day — the
              // gesture people already expect from a calendar. On a day there is
              // nowhere to go, and a control that does nothing is worse than a
              // label.
              return week ? (
                <button
                  key={dayKey(day)}
                  type="button"
                  onClick={() => onOpenDay(day)}
                  aria-label={t("calendar.openDay", { date: formatDayShort(day) })}
                  className="flex flex-col items-center gap-0.5 py-2 transition-colors hover:bg-surface-alt"
                >
                  {heading}
                </button>
              ) : (
                <span key={dayKey(day)} className="flex flex-col items-center gap-0.5 py-2">
                  {heading}
                </span>
              );
            })}
          </div>
        </div>

        {band.rows > 0 ? (
          <div className="flex border-b border-line">
            <span
              aria-hidden="true"
              className="shrink-0 self-center px-1.5 text-right text-[0.625rem] leading-tight text-muted"
              style={{ width: GUTTER }}
            >
              {t("common.allDay")}
            </span>
            {/* Capped, then scrolled: a week somebody filled with all-day
                entries must not push the hours off the screen entirely. */}
            <div
              className="grid max-h-[102px] flex-1 gap-1 overflow-y-auto py-1.5 pr-1.5"
              style={{
                gridTemplateColumns: track(span),
                gridTemplateRows: `repeat(${band.rows}, minmax(24px, auto))`,
              }}
            >
              {band.entries.map((entry) => (
                <button
                  key={entry.event.key}
                  type="button"
                  onClick={() => onOpen(entry.event.key)}
                  aria-label={t("calendar.detailsOf", { title: entry.event.title })}
                  style={{
                    gridColumn: `${entry.start + 1} / ${entry.end + 2}`,
                    gridRow: entry.row + 1,
                  }}
                  className={cn(
                    "truncate rounded-[10px] px-2 text-left text-[0.75rem] leading-[24px] transition-colors",
                    opened === entry.event.key
                      ? "bg-accent-soft font-medium text-accent-ink"
                      : "bg-surface-alt text-ink-soft hover:bg-surface",
                  )}
                >
                  {entry.event.title}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* The scale. Bounded here rather than left to grow to its full 1248px:
            the page scrolls too, and a grid taller than the screen would make
            the two of them fight. */}
        <div
          ref={scroller}
          className="overflow-y-auto overscroll-contain"
          style={{ height: "clamp(300px, calc(100dvh - 27rem), 620px)" }}
        >
          <div className="relative flex" style={{ height: DAY_MINUTES * MINUTE_HEIGHT }}>
            <div
              aria-hidden="true"
              className="relative shrink-0"
              style={{ width: GUTTER }}
            >
              {HOURS.map((hour) => (
                <span
                  key={hour}
                  className="tabular absolute right-1.5 -translate-y-1/2 text-[0.625rem] text-muted"
                  style={{ top: hour * HOUR_HEIGHT }}
                >
                  {formats.time.format(new Date(2024, 0, 1, hour))}
                </span>
              ))}
            </div>

            <div className="relative flex-1">
              {/* The weakest thing on the screen, and drawn once across every
                  column rather than per day — seven sets of the same twenty-three
                  lines would be ruling, not a scale. */}
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  aria-hidden="true"
                  // `line`, not `line-soft`: on a surface in the dark theme
                  // the soft one is a single step per channel, which is no
                  // line at all. This is still the faintest thing on screen.
                  className="absolute inset-x-0 border-t border-line"
                  style={{ top: hour * HOUR_HEIGHT }}
                />
              ))}

              <div className="grid h-full" style={{ gridTemplateColumns: track(span) }}>
                {days.map((day, index) => (
                  <div key={dayKey(day)} className="relative">
                    {/* Behind everything: the blocks come after it in the DOM,
                        so a tap on an event is a tap on the event. */}
                    <button
                      type="button"
                      onClick={(press) => composeAt(day, press)}
                      aria-label={t("calendar.addOn", { date: formatDayShort(day) })}
                      className="absolute inset-0 w-full"
                    />

                    {columns[index]!.blocks.map((block) => (
                      <EventBlock
                        key={block.event.key}
                        block={block}
                        open={opened === block.event.key}
                        onOpen={onOpen}
                      />
                    ))}

                    {sameDay(day, today) ? <NowLine now={now} /> : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {openedEvent ? (
        <EventSheet
          event={openedEvent}
          spaceId={spaceId}
          self={self}
          onSave={(draft, scope) => {
            onSave(openedEvent, draft, scope);
            onOpen(null);
          }}
          onClose={() => onOpen(null)}
        />
      ) : null}
    </div>
  );
}

/** Equal columns that are allowed to be narrower than their content. */
function track(span: number): string {
  return `repeat(${span}, minmax(0, 1fr))`;
}

/**
 * One event, at its hour and for its length.
 *
 * A block is content, not a control, so it is allowed under the 44px floor —
 * a half-hour has to look like half an hour or the whole scale is a lie. The
 * radius is its own, smaller than a row's: at 26px tall a 16px corner turns a
 * block into a lozenge and the grid reads as a bag of pills.
 *
 * The time it prints is the event's, never the clipped segment's, so the half
 * of a night out that lands on Sunday still says it started at ten on Saturday.
 */
function EventBlock({
  block,
  open,
  onOpen,
}: {
  block: Block;
  open: boolean;
  onOpen: (key: string) => void;
}) {
  const t = useT();

  const height = Math.max(MIN_BLOCK, (block.to - block.from) * MINUTE_HEIGHT);
  const width = 100 / block.columns;

  return (
    <button
      type="button"
      onClick={() => onOpen(block.event.key)}
      aria-label={t("calendar.detailsOf", { title: block.event.title })}
      style={{
        top: block.from * MINUTE_HEIGHT,
        height,
        // The two pixels are the gap between neighbours — a lane boundary read
        // as space, since there is no line to read it as.
        left: `calc(${block.column * width}% + 2px)`,
        width: `calc(${width}% - 4px)`,
      }}
      className={cn(
        "absolute overflow-hidden rounded-[10px] px-1.5 py-0.5 text-left transition-colors",
        open
          ? "bg-accent-soft text-accent-ink"
          // Hovering steps the other way, like the date plate does: the block
          // is already the raised tone, so there is nowhere up to go.
          : "bg-surface-alt text-ink hover:bg-surface",
      )}
    >
      <span className="block truncate text-[0.75rem] leading-[1.2] font-medium">
        {block.event.title}
      </span>
      {/* Only when there is room for a second line. Squeezing 19:00 into a
          twenty-six pixel block clips the title, which is the half that says
          what the thing is. */}
      {height >= 42 ? (
        <span className="tabular mt-0.5 block truncate text-[0.6875rem] opacity-70">
          {formatEventTime(block.event)}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Сейчас.
 *
 * A hairline and a dot, in the one accent this language has — the same amber
 * that means "today" on the grid above it. Drawn only in today's column, so on
 * a week it also says which column that is.
 */
function NowLine({ now }: { now: Date }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 z-10"
      style={{ top: (now.getHours() * 60 + now.getMinutes()) * MINUTE_HEIGHT }}
    >
      <span className="block h-px bg-accent" />
      <span className="absolute -top-[3px] left-0 size-[7px] rounded-pill bg-accent" />
    </div>
  );
}

/**
 * The files waiting for an event that does not exist yet.
 *
 * Deliberately not an `AttachmentStrip` — see the note where these are held.
 * The same row of chips the message composer uses, for the same reason: the
 * person needs to see what they picked, and be able to change their mind,
 * before anything is sent anywhere.
 */
/**
 * How close the calendar is standing: a day, a week, a month.
 *
 * It rides in the period bar rather than getting a row of its own. A row of its
 * own is what it had — as three of four equal tabs — and on a phone that plus
 * the heading plus the bar left under two hours of the day visible on the very
 * views that exist to show hours.
 *
 * Small on purpose. This is not the question the screen is about; it is the
 * lens, and a lens does not need to be the size of the thing it looks at.
 */
function ZoomSwitch({
  zoom,
  onZoom,
}: {
  zoom: Exclude<View, "agenda">;
  onZoom: (next: Exclude<View, "agenda">) => void;
}) {
  const t = useT();

  return (
    /*
     * A row of its own, under the title, and therefore full width.
     *
     * It used to sit on the same line as the arrows, «Сегодня» and the period —
     * and the fixed parts of that line added up to more than a 390px phone has,
     * so the only flexible thing on it got squeezed: the period. Which is the
     * one part that says *which* week you are looking at, and it collapsed
     * exactly when you had scrolled away from today, since that is when
     * «Сегодня» appears and takes its width.
     */
    <div
      role="tablist"
      aria-label={t("calendar.zoomLabel")}
      className="flex min-w-0 flex-1 gap-0.5 rounded-pill bg-surface-alt p-0.5"
    >
      {ZOOMS.map((value) => (
        <button
          key={value}
          role="tab"
          aria-selected={value === zoom}
          onClick={() => onZoom(value)}
          className={cn(
            "min-h-[34px] flex-1 rounded-pill px-2 text-[0.8125rem] transition-colors",
            value === zoom
              ? "bg-accent-soft font-semibold text-accent-ink"
              : "font-medium text-muted hover:text-ink-soft",
          )}
        >
          {t(VIEW_LABEL[value])}
        </button>
      ))}
    </div>
  );
}

function PendingFiles({
  files,
  onChange,
}: {
  files: File[];
  onChange: (next: File[]) => void;
}) {
  const t = useT();
  const picker = useRef<HTMLInputElement | null>(null);

  return (
    <div className="flex flex-col gap-2">
      {files.length > 0 ? (
        <ul aria-label={t("attachments.picked")} className="flex list-none flex-wrap gap-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="flex min-h-[var(--touch)] max-w-full items-center gap-1.5 rounded-pill bg-surface-alt pr-0.5 pl-4"
            >
              <span className="truncate text-[0.8125rem] text-ink-soft">{file.name}</span>
              <button
                type="button"
                onClick={() => onChange(files.filter((_, at) => at !== index))}
                aria-label={t("attachments.unpick", { name: file.name })}
                className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:text-neg"
              >
                <CloseIcon className="size-[18px]" strokeWidth={1.9} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        onClick={() => picker.current?.click()}
        className="inline-flex min-h-[var(--touch)] w-fit items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] text-muted transition-colors hover:text-accent"
      >
        <PlusIcon className="size-[18px]" strokeWidth={1.9} />
        {t("attachments.addFile")}
      </button>

      <input
        ref={picker}
        type="file"
        multiple
        accept={ACCEPT_ATTACHMENTS}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          // Cleared first, so choosing the same file twice in a row still
          // fires a change event.
          e.target.value = "";
          if (picked.length > 0) onChange([...files, ...picked]);
        }}
        className="hidden"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ form */

/**
 * One form for creating and for editing.
 *
 * The date and time inputs are the native ones. They render in the device's own
 * locale and open the platform pickers — a hand-rolled Russian date field would
 * be worse on every phone it ran on.
 */
function EventForm({
  id,
  draft,
  submitLabel,
  notice,
  autoFocus = true,
  onChange,
  onSubmit,
  onCancel,
  children,
}: {
  /** So a submit button outside the form can reach it (`form="…"`). */
  id?: string;
  draft: EventDraft;
  /**
   * The inline submit's label — or null when the button that saves this form
   * lives outside it, as the sheet's «Готово» does.
   */
  submitLabel: string | null;
  /** Something true about this event the form itself cannot express. */
  notice?: string | undefined;
  /** Whether the title takes focus on mount. See the sheet for why it does not. */
  autoFocus?: boolean;
  onChange: (draft: EventDraft) => void;
  onSubmit: (draft: EventDraft) => void;
  onCancel: () => void;
  /**
   * What belongs to the event but not to the draft. Only the composer has any
   * now — the files somebody picked before the event existed, which have
   * nowhere else to wait. The sheet's own files, roll call, discussion and
   * skip are siblings of this form rather than children of it, so that
   * switching between reading and editing leaves them mounted.
   */
  children?: ReactNode;
}) {
  const t = useT();
  const [touched, setTouched] = useState(false);
  const error = draftError(draft);

  /*
   * Everything past "what and when", folded away until it is wanted.
   *
   * The form opened with ten fields at once — repeat as six pills, a link, a
   * place, a note, the files — which is a screenful of nothing for «ужин в
   * 19:00», and a calendar you stop keeping is one that asked too much every
   * time. The wishes screen has answered the same question for a while: lead
   * with the field that is always filled in, and let the rest be asked for.
   *
   * Open from the start whenever any of it is already filled in, which is what
   * editing an existing event usually means. Folding away a note somebody
   * wrote would be hiding the event from the person looking at it.
   */
  const [more, setMore] = useState(
    () =>
      draft.rrule !== null ||
      draft.url.trim() !== "" ||
      draft.location.trim() !== "" ||
      draft.note.trim() !== "",
  );

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (error) return;
    onSubmit(draft);
  }

  const set = (patch: Partial<EventDraft>) => onChange({ ...draft, ...patch });

  return (
    <form id={id} onSubmit={submit} className="flex flex-col gap-3 p-4">
      <Field label={t("calendar.fieldTitle")}>
        <input
          value={draft.title}
          onChange={(e) => set({ title: e.target.value })}
          placeholder={t("calendar.titlePlaceholder")}
          autoFocus={autoFocus}
          className={INPUT}
        />
      </Field>

      <Field label={t("calendar.fieldDate")}>
        <input
          type="date"
          value={draft.date}
          onChange={(e) => set({ date: e.target.value })}
          className={INPUT}
        />
      </Field>

      {/* All-day changes what is shown, not when the event is: the timestamp
          keeps the time it already had. */}
      <button
        type="button"
        role="switch"
        aria-checked={draft.allDay}
        onClick={() => set({ allDay: !draft.allDay })}
        className="flex min-h-[var(--touch)] items-center justify-between gap-3 rounded-row bg-surface-alt px-3.5 transition-colors"
      >
        <span className="text-[0.9375rem]">{t("calendar.fieldAllDay")}</span>
        <span
          aria-hidden="true"
          className={cn(
            "relative h-6 w-11 shrink-0 rounded-pill transition-colors",
            draft.allDay ? "bg-accent" : "bg-line",
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 size-5 rounded-pill bg-surface shadow-soft transition-all",
              draft.allDay ? "left-[22px]" : "left-0.5",
            )}
          />
        </span>
      </button>

      {draft.allDay ? null : (
        <div className="flex gap-3">
          <Field label={t("calendar.fieldStart")} className="min-w-0 flex-1">
            <input
              type="time"
              value={draft.startTime}
              onChange={(e) => set({ startTime: e.target.value })}
              className={INPUT}
            />
          </Field>
          <Field label={t("calendar.fieldEnd")} hint={t("common.optional")} className="min-w-0 flex-1">
            <input
              type="time"
              value={draft.endTime}
              onChange={(e) => set({ endTime: e.target.value })}
              className={INPUT}
            />
          </Field>
        </div>
      )}

      {more ? null : (
        <button
          type="button"
          onClick={() => setMore(true)}
          className="inline-flex min-h-[var(--touch)] w-fit items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] font-medium text-muted transition-colors hover:text-ink-soft"
        >
          <ChevronDownIcon aria-hidden="true" className="size-[18px]" strokeWidth={1.9} />
          {t("calendar.more")}
        </button>
      )}

      {more ? (
        <>
          <RepeatField draft={draft} onChange={(rrule) => set({ rrule })} />

          <ReminderField
            minutes={draft.reminderLeadMinutes}
            onChange={(reminderLeadMinutes) => set({ reminderLeadMinutes })}
          />

          <Field label={t("calendar.fieldUrl")} hint={t("calendar.urlHint")}>
            <input
              type="url"
              inputMode="url"
              value={draft.url}
              onChange={(e) => set({ url: e.target.value })}
              placeholder="https://"
              className={INPUT}
            />
          </Field>

          {/* Где. It was readable on the sheet and settable nowhere — the value
              round-tripped through the draft so that an edit would not delete
              one somebody else had set, which is a strange thing to protect
              when the only way to set it was another client. */}
          <Field label={t("calendar.location")} hint={t("common.optional")}>
            <input
              value={draft.location}
              onChange={(e) => set({ location: e.target.value })}
              placeholder={t("calendar.locationPlaceholder")}
              className={INPUT}
            />
          </Field>

          <Field label={t("calendar.fieldNote")} hint={t("common.optional")}>
            <textarea
              value={draft.note}
              onChange={(e) => set({ note: e.target.value })}
              rows={2}
              placeholder={t("calendar.notePlaceholder")}
              className={cn(INPUT, "min-h-[68px] resize-y")}
            />
          </Field>
        </>
      ) : null}

      {/* Outside the fold: the files somebody picked before the event existed
          are already on screen, and hiding them would be losing them. */}
      {children}

      {notice ? (
        <p className="rounded-row bg-warn-soft px-3.5 py-2 text-[0.8125rem] text-warn">
          {notice}
        </p>
      ) : null}

      {touched && error ? (
        <p role="alert" className="px-1 text-[0.8125rem] text-neg">
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        {submitLabel === null ? null : (
          <Button type="submit" disabled={!draft.title.trim()}>
            {submitLabel}
          </Button>
        )}
        {/* On the sheet this stands alone, and it does not close anything: it
            gives back the event as it is saved. */}
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

/**
 * When to speak up about this one, if the usual answer is wrong for it.
 *
 * «Как обычно» is the first option and the default, because the notice belongs
 * to the person (SPEC §4.7) and this only overrides it. What forces the
 * override to exist is that a household has exactly two kinds of event and one
 * number cannot serve both: a birthday wants two days, dinner at seven wants
 * half an hour, and whichever is set in the settings is wrong for half the
 * calendar.
 *
 * The same presets the settings offer, plus a day and two — the long end is
 * exactly what a single shared number could not reach without ruining the
 * short end.
 */
const EVENT_LEADS: readonly (number | null)[] = [null, 0, 15, 60, 120, 1440, 2880];

function ReminderField({
  minutes,
  onChange,
}: {
  minutes: number | null;
  onChange: (minutes: number | null) => void;
}) {
  const t = useT();
  const labelId = useId();

  /* A `<span>` and `aria-labelledby`, not the `<label>` that `Field` is: a
     label wrapping a row of buttons hands its own words to every one of them,
     so each pill would announce «Напомнить», not «за 2 дня». The repeat row
     above solved this the same way. */
  return (
    <div className="flex flex-col gap-1.5">
      <span id={labelId} className="px-1 text-[0.8125rem] font-medium text-ink-soft">
        {t("calendar.fieldReminder")}{" "}
        <span className="font-normal text-muted">({t("calendar.reminderHint")})</span>
      </span>

      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="flex flex-wrap gap-1.5"
      >
        {EVENT_LEADS.map((value) => (
          <button
            key={value ?? "usual"}
            type="button"
            role="radio"
            aria-checked={minutes === value}
            onClick={() => onChange(value)}
            className={cn(
              "min-h-[var(--touch)] rounded-pill px-3.5 text-[0.9375rem] transition-colors",
              minutes === value
                ? "bg-accent-soft font-semibold text-accent-ink"
                : "bg-surface-alt font-medium text-muted hover:text-ink-soft",
            )}
          >
            {value === null ? t("calendar.reminderUsual") : leadWords(t, value)}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- repeat */

/**
 * The six rules this form offers — and the seventh state, which it does not.
 *
 * `own` is an existing rule the pills cannot say: anything `parseRecurrence`
 * refuses outright, and anything it reads but no pill stands for — every second
 * Tuesday, a series that stops in May. It is shown as its own selected option
 * and the draft keeps the rule untouched, because the alternative is that
 * opening a form to fix a typo quietly rewrites when the event happens.
 *
 * Distinct from `none`, which is a real choice and means `rrule === null`.
 */
type Repeat = "none" | "daily" | "weekdays" | "weekly" | "monthly" | "custom" | "own";

const WORKWEEK: readonly Weekday[] = ["MO", "TU", "WE", "TH", "FR"];

/** The six, in the order they are offered. `own` joins them only when it exists. */
const REPEAT_LABEL: readonly { value: Repeat; key: MessageKey }[] = [
  { value: "none", key: "calendar.repeatNever" },
  { value: "daily", key: "calendar.repeatDaily" },
  { value: "weekdays", key: "calendar.repeatWeekdays" },
  { value: "weekly", key: "calendar.repeatWeekly" },
  { value: "monthly", key: "calendar.repeatMonthly" },
  { value: "custom", key: "calendar.repeatCustom" },
];

/**
 * The rule string for a choice.
 *
 * Written by `shared` rather than assembled here, so the one spelling of a rule
 * is the one both sides already agree on — and so an empty `byWeekday` produces
 * the bare `FREQ=WEEKLY` the RFC means by "the day the event starts on".
 */
function ruleFor(
  freq: Frequency,
  byWeekday: Weekday[] = [],
  /**
   * Days already cancelled, carried across a change of rule.
   *
   * Switching «по будням» to «каждый день» is a change to *when* it repeats,
   * not permission to reinstate a day somebody said is not happening. Dropping
   * them here would put a cancelled event quietly back on the calendar.
   */
  exceptions: Date[] = [],
): string {
  return formatRecurrence({ freq, interval: 1, byWeekday, until: null, count: null, exceptions });
}

/** Which option a stored rule is, and the days it names. */
function readRepeat(rrule: string | null): { choice: Repeat; days: Weekday[] } {
  if (!rrule) return { choice: "none", days: [] };

  const rule = parseRecurrence(rrule);

  // Two different ways of being unshowable, one state: outside the subset
  // `shared` reads at all, or inside it and past what a pill can say.
  if (!rule || rule.interval !== 1 || rule.until || rule.count) {
    return { choice: "own", days: [] };
  }

  if (rule.freq === "daily") return { choice: "daily", days: [] };
  if (rule.freq === "monthly") return { choice: "monthly", days: [] };
  if (rule.byWeekday.length === 0) return { choice: "weekly", days: [] };

  // Monday-first, like `WEEKDAYS` and like the row of day chips below.
  const days = WEEKDAYS.filter((day) => rule.byWeekday.includes(day));
  const workweek =
    days.length === WORKWEEK.length && days.every((day) => WORKWEEK.includes(day));

  return { choice: workweek ? "weekdays" : "custom", days };
}

/** The weekday the draft currently falls on; Monday if the date is unusable. */
function weekdayOfDraft(date: string): Weekday {
  const [year, month, day] = date.split("-").map(Number);
  const at = new Date(year ?? NaN, (month ?? 1) - 1, day ?? 1);
  return Number.isNaN(at.getTime()) ? "MO" : weekdayOf(at);
}

/**
 * Повтор.
 *
 * A wrapping row of pills rather than a strip that scrolls sideways: there are
 * six of them and a narrow form, and an option hidden past the right edge is an
 * option nobody finds.
 */
function RepeatField({
  draft,
  onChange,
}: {
  draft: EventDraft;
  onChange: (rrule: string | null) => void;
}) {
  const t = useT();
  const names = useWeekdayNames();
  const labelId = useId();

  const { choice: stored, days } = readRepeat(draft.rrule);

  /*
   * Held for as long as the form is open rather than re-read from the draft:
   * the moment one of the six is pressed the old rule leaves the draft, and the
   * pill has to stay on the row for changing one's mind back to be possible.
   */
  const [own] = useState<string | null>(() =>
    readRepeat(draft.rrule).choice === "own" ? draft.rrule : null,
  );

  /*
   * «По будням» and «Свои дни» with Mon–Fri ticked are the same rule, so which
   * of the two is lit cannot be read back out of the string. This remembers
   * which was pressed; everything else on this control is derived.
   */
  /** Days already cancelled on this event, so a change of rule keeps them. */
  const kept = parseRecurrence(draft.rrule)?.exceptions ?? [];

  const [picking, setPicking] = useState(stored === "custom");
  const choice: Repeat = picking && stored === "weekdays" ? "custom" : stored;

  function pick(next: Repeat) {
    setPicking(next === "custom");

    switch (next) {
      case "none":
        return onChange(null);
      case "daily":
        return onChange(ruleFor("daily", [], kept));
      case "weekdays":
        return onChange(ruleFor("weekly", [...WORKWEEK], kept));
      case "weekly":
        return onChange(ruleFor("weekly", [], kept));
      case "monthly":
        return onChange(ruleFor("monthly", [], kept));
      case "custom":
        // Opening the panel with nothing ticked would write a bare FREQ=WEEKLY,
        // which is «каждую неделю» — the option next door. The event's own
        // weekday is precisely what that bare rule means, so it is the honest
        // first tick.
        return onChange(
          ruleFor("weekly", days.length > 0 ? days : [weekdayOfDraft(draft.date)], kept),
        );
      case "own":
        return onChange(own);
    }
  }

  function toggleDay(day: Weekday) {
    const next = days.includes(day) ? days.filter((on) => on !== day) : [...days, day];

    // The last tick stays put. Zero days is a bare FREQ=WEEKLY, which would
    // move the selection to a different pill on the same row — untick the last
    // day and the panel you were using would close under your thumb.
    if (next.length === 0) return;

    setPicking(true);
    onChange(ruleFor("weekly", next, kept));
  }

  const options = own
    ? [...REPEAT_LABEL, { value: "own" as const, key: "calendar.repeatOwn" as const }]
    : REPEAT_LABEL;

  return (
    <div className="flex flex-col gap-1.5">
      <span id={labelId} className="px-1 text-[0.8125rem] font-medium text-ink-soft">
        {t("calendar.fieldRepeat")}
      </span>

      <div role="radiogroup" aria-labelledby={labelId} className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={option.value === choice}
            onClick={() => pick(option.value)}
            className={cn(
              "min-h-[var(--touch)] rounded-pill px-3.5 text-[0.9375rem] transition-colors",
              option.value === choice
                ? "bg-accent-soft font-semibold text-accent-ink"
                : "bg-surface-alt font-medium text-muted hover:text-ink-soft",
            )}
          >
            {t(option.key)}
          </button>
        ))}
      </div>

      {/* Seven columns edge to edge, the same shape as the month grid's week —
          a wrapping row would break Пн–Вс across two lines and stop reading as
          a week at all. */}
      {choice === "custom" ? (
        <div
          role="group"
          aria-label={t("calendar.repeatDaysLabel")}
          className="mt-0.5 grid grid-cols-7 gap-1"
        >
          {WEEKDAYS.map((day, index) => {
            const on = days.includes(day);

            return (
              <button
                key={day}
                type="button"
                aria-pressed={on}
                onClick={() => toggleDay(day)}
                className={cn(
                  "min-h-[var(--touch)] rounded-pill text-[0.8125rem] transition-colors",
                  on
                    ? "bg-accent-soft font-semibold text-accent-ink"
                    : "bg-surface-alt text-muted hover:text-ink-soft",
                )}
              >
                {names[index]}
              </button>
            );
          })}
        </div>
      ) : null}

      {choice === "own" ? (
        <p className="px-1 text-[0.8125rem] text-muted">{t("calendar.repeatOwnHint")}</p>
      ) : null}
    </div>
  );
}

/**
 * The same filled field `TextField` builds, for the inputs this form has to
 * make itself — a textarea, and the native date and time inputs, which need
 * their own value handling.
 *
 * Filled rather than outlined, and one tonal step off the card that holds it:
 * an outline here would be the only drawn line on the screen, and the form
 * would stop looking like it belonged to the rest of the app.
 */
const INPUT =
  "min-h-[var(--touch)] w-full rounded-row bg-surface-alt px-4 py-2.5 transition-colors outline-none placeholder:text-muted";

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="px-1 text-[0.8125rem] font-medium text-ink-soft">
        {label}
        {hint ? <span className="ml-1.5 font-normal text-muted">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}
